"use client";

import { useState, useEffect, useRef, useMemo } from "react";
import { Button } from "../ui/button";
import { UnifiedCard, UnifiedCardContent } from "../ui/unified-card";
import { FormVersionDetails, FormPage, FormField, FormSection as FormSectionType } from "../../types/form-types";
import { Loader2, ArrowRight, ArrowLeft } from "lucide-react";
import { toast } from "react-toastify";
import { CameraModal } from "../CameraModal";
import { PaymentModal } from "../PaymentModal";
import { ConflictResolutionModal } from "../ConflictResolutionModal";
import FormFieldRenderer from "../FormFieldRenderer";
import { CapturedImage, Field } from "../../types/types";

// Runtime type after FormField -> Field conversion at API boundary
interface RuntimeFormVersionDetails extends Omit<FormVersionDetails, 'form_structure'> {
  form_structure: {
    pages: FormPage[];
    fields: Field[]; // Converted from FormField[] at API boundary
    sections?: FormSectionType[];
    theme?: Theme; // Theme data embedded in version
    theme_config?: Record<string, any>;
  };
}
import { getResolvedSilhouetteUrl } from "../utils/silhouetteUtils";
import Webcam from "react-webcam";
import { evaluateFieldConditions } from "../../utils/fieldConditions";
import { evaluatePageConditions, findNextVisiblePage, getVisiblePages } from "../../utils/pageConditions";
import { formPagesToPages, formFieldsToFields } from "../../utils/typeConversion";
import { postWithAuth } from "../../utils/auth";
import { useFormBuilderContext } from "../../contexts/FormBuilderContext";
import { API_BASE_URL } from "../../environments/environment";
import { getFieldContainerClasses } from "../../utils/fieldLayoutUtils";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { FormSection } from "../form-builder/FormSection";
import { getSectionsByPage, getFieldsBySection, getUnsectionedFields } from "../../utils/sectionUtils";
import { useFormSession, SessionModalConfig } from "../../hooks/useFormSession";

// Import new utility files
import { getAnswersFromFormAnswers, extractAnswerValue, mapInitialValuesToFields } from "./formAnswerUtils";
import { cleanupHiddenFieldAnswers } from "./fieldVisibilityUtils";
import { calculateLastAnsweredPage } from "./pageNavigationUtils";
import { getPaymentAmount, getMappedPaymentValues, autoFillCustomerFields } from "./paymentFieldUtils";
import { preparePageAnswersPayload, ensureBooleanFieldDefaults, getPageFieldIds, findPhoneField } from "./pageSubmissionUtils";
import { getImageUploadFields, handleImageUploads } from "./imageUploadUtils";
import { validateField } from "../../utils/fieldValidation";
import { getAccessToken } from "../../utils/auth";
import { resolveAssetUrl } from "../../utils/assetUrls";
import { FormPageRenderer } from "./FormPageRenderer";
import { cacheSubmission, removeCachedSubmission } from "../../services/submissionCache";
import { persistFormAnswers, createFormEntryForPersist, getSavedAnswersBySession } from "../../services/formEntries";
import { getFilteredParams, getEmbedSearchString } from "../../utils/url-params";
import { StylingProvider, useStyling } from "../../contexts/StylingContext";
import type { Theme } from "../../types/theme-types";

interface FormRendererProps {
  formId?: string | number;
  formVersionId?: number; // Alternative to formId - directly specify version
  formEntryId?: string | number;
  sessionId?: string;
  userId?: string;
  onComplete?: (data: any) => void | Promise<void>;
  onPageChange?: (pageIndex: number) => void;
  onPageComplete?: (data: { pageIndex: number; pageData: any; allAnswers: any }) => void | Promise<void>;
  onCancel?: () => void;
  onError?: (error: string) => void;
  onFormReset?: () => void;
  readOnly?: boolean;
  className?: string;
  initialValues?: Record<string, any>; // Pre-populate form with initial values
  customEndpointPath?: string; // Optional path to send answers to client's backend. When provided, Form Builder DB is skipped unless alsoSaveToFormBuilder=true
  alsoSaveToFormBuilder?: boolean; // When customEndpointPath is provided, set true to ALSO save to Form Builder DB. Default: false (client DB only)
  updateSessionProgressEndpointPath?: string; // Optional path to update last_page_answered in form_entry after each page submission
  fetchFormSessionEndpointPath?: string; // Optional path to fetch previous form session data
  deleteSessionStorage?: boolean; // Whether to delete form_session_id from localStorage when form completes (default: true)
  askBeforeContinuingSession?: boolean | SessionModalConfig; // Show modal asking user to continue or start new session
  embeddedMode?: boolean; // Enable minimal styling for iframe embedding
  embedRedirectUrl?: string; // Redirect URL for parent page after form completion
  formAnswers?: Record<string, any>; // Form answers from client's backend (primary source of truth)
  onStartNewSession?: () => void; // Callback when user wants to start a new session (for backend-managed sessionId)
  onHeightChange?: (height: number) => void; // Callback when form height changes (for iframe auto-resize)
  staticFormVersion?: FormVersionDetails; // Optional static form version data (bypasses API call)
  payloadTransformer?: (payload: any) => any; // Optional function to transform payload before sending to customEndpointPath
  formConfig?: any; // Optional form structure from client's backend (bypasses API call to fetch form structure)
  onPaymentVerified?: (verificationResult: any) => void; // Callback fired with the full verification JSON after a successful payment (use to extract status, transactionId, etc.)
  autoPopulateFromUrl?: boolean; // Automatically extract and map parameters from URL query string
  theme?: Theme; // Optional theme data for dynamic branding
  chameleonMode?: boolean; // Whether to enable Chameleon Mode (auto-branding based on context)
  /** Controls page navigation UI. 'sequential' = Previous/Next buttons (default). 'tabs' = clickable page pill tabs (for preview). */
  navigationMode?: 'sequential' | 'tabs';
}

// Converts flattened form structure to RuntimeFormVersionDetails
// Performs FormField -> Field conversion at API boundary for type safety

const buildThemeFromConfig = (
  themeConfig: Record<string, any> | undefined,
  formVersion?: RuntimeFormVersionDetails | FormVersionDetails | null,
  applicationId?: string
): Theme | null => {
  if (!themeConfig || Object.keys(themeConfig).length === 0) return null;

  return {
    id: 0,
    name: "form-version-theme",
    application_id: applicationId,
    config: {
      colors: themeConfig.colors ?? {},
      typography: themeConfig.typography ?? {},
      spacing: {},
      borderRadius: {},
      shadows: {},
      buttons: {},
      inputs: {},
      custom: {},
      hide_branding: themeConfig.hide_branding === true,
      custom_css: themeConfig.custom_css ?? "",
      header: themeConfig.header,
      layout: themeConfig.layout,
    },
    assets: {
      logo: resolveAssetUrl(themeConfig.assets?.logo),
      background: resolveAssetUrl(themeConfig.assets?.background),
    },
    metadata: {
      form_version_id: formVersion?.id,
    },
    is_active: true,
    is_default: false,
  };
};

/**
 * ThemedRendererRoot
 *
 * Inner component that reads cssVariables from StylingProvider context
 * and applies them directly as inline styles on the root wrapper element.
 * This correctly scopes CSS custom properties to the form renderer only,
 * without leaking via CSS variable inheritance into parent/sibling components.
 */
const ThemedRendererRoot: React.FC<{ children: React.ReactNode; className?: string; embedded?: boolean }> = ({
  children,
  className = '',
  embedded = false,
}) => {
  const { cssVariables, theme } = useStyling();
  const customCss = theme?.config?.custom_css || "";

  return (
    <div
      data-theme-wrapper=""
      data-embedded={embedded ? "true" : undefined}
      className={`form-theme-wrapper form-renderer-container relative ${className}`}
      style={cssVariables as React.CSSProperties}
    >
      {customCss && <style data-form-version-custom-css>{customCss}</style>}
      {children}
    </div>
  );
};

const convertToFormVersionDetails = (data: any, formId?: string | number): RuntimeFormVersionDetails => {
  const sourceStructure = data.form_structure || data;

  return {
    id: data.id || formId || 0,
    name: data.name || data.versionName || 'Form',
    description: data.description || data.versionDescription || '',
    version: data.version || 1,
    status: data.status || 'published',
    form_structure: {
      ...sourceStructure,
      pages: sourceStructure.pages || [],
      fields: formFieldsToFields(sourceStructure.fields || []), // Convert FormField[] to Field[] at API boundary
      sections: sourceStructure.sections || []
    },
    template: data.template,
    date_created: data.date_created || new Date().toISOString(),
    date_updated: data.date_updated || data.lastModified || new Date().toISOString(),
    is_payment_form: data.is_payment_form || false,
    webhook_call_on_page_submit: data.webhook_call_on_page_submit || false,
    external_storage_config: data.external_storage_config,
    form: {
      id: data.form_id || data.form?.id || data.form || data.id || formId || 0,
      name: data.name || data.versionName || 'Form',
      description: data.description || data.versionDescription || '',
    },
  };
};

export function FormRenderer({
  formId,
  formVersionId: propFormVersionId,
  formEntryId: propFormEntryId,
  sessionId: propSessionId,
  userId: propUserId,
  onComplete,
  onPageChange,
  onPageComplete,
  onCancel,
  onError,
  onFormReset,
  readOnly = false,
  className = "",
  initialValues = {},
  formAnswers = {},
  customEndpointPath,
  alsoSaveToFormBuilder = false,
  updateSessionProgressEndpointPath,
  fetchFormSessionEndpointPath,
  deleteSessionStorage = true,
  askBeforeContinuingSession = false,
  embeddedMode = false,
  embedRedirectUrl,
  onStartNewSession,
  onHeightChange,
  staticFormVersion,
  payloadTransformer,
  formConfig,
  onPaymentVerified,
  autoPopulateFromUrl = false,
  theme,
  chameleonMode = false,
  navigationMode = 'sequential',
}: FormRendererProps) {
  // Get app config and applicationId from context
  const { appConfig, applicationId } = useFormBuilderContext();

  // Parse session modal config
  const sessionModalConfig: SessionModalConfig = typeof askBeforeContinuingSession === 'object'
    ? { ...askBeforeContinuingSession, enabled: true } // enabled: true comes last to ensure it's set
    : { enabled: askBeforeContinuingSession };

  // State management - declare early before hooks that depend on them
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formVersion, setFormVersion] = useState<RuntimeFormVersionDetails | null>(null);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [answers, setAnswers] = useState<Map<string, any>>(new Map()); // fieldId -> value

  // ── answersRef ──────────────────────────────────────────────────────
  // Mirror of `answers` that always holds the latest Map, so async callbacks
  // (payment verification, subscription bypass) can read current state without
  // capturing a stale closure. Without this, `new Map(answers)` inside an async
  // handler snapshots the answers as they were when the handler was created —
  // not when it fires — and a checkbox the user toggled while the Paystack
  // popup was open gets wiped when setAnswers(finalAnswers) runs.
  const answersRef = useRef(answers);
  useEffect(() => { answersRef.current = answers; }, [answers]);
  const [error, setError] = useState<string>("");
  const [loadedFormAnswers, setLoadedFormAnswers] = useState<Record<string, any>>(formAnswers || {}); // Local state for fetched/provided answers
  const [isFetchingAnswers, setIsFetchingAnswers] = useState(false);
  const effectiveTheme = useMemo(() => {
    if (theme) return theme;
    const themeConfig = (formVersion?.form_structure as any)?.theme_config;
    return buildThemeFromConfig(themeConfig, formVersion, applicationId);
  }, [theme, formVersion, applicationId]);

  // Check if the theme or form configuration hide toggle is active.
  const isBrandingHiddenByConfig = (() => {
    // Check theme config
    if (effectiveTheme?.config) {
      const themeConfig = effectiveTheme.config as any;
      if (
        themeConfig.hide_branding === true ||
        themeConfig.hide_powered_by === true
      ) {
        return true;
      }
    }

    // Check theme config custom
    if (effectiveTheme?.config?.custom && typeof effectiveTheme.config.custom === 'object') {
      if (effectiveTheme.config.custom.hide_branding === true) {
        return true;
      }
    }

    // Check formVersion config
    const formSettings = (formVersion?.form_structure as any)?.settings;
    if (formSettings) {
      if (formSettings.hide_branding === true) {
        return true;
      }
    }

    // Check direct formVersion structure config
    const formStructure = formVersion?.form_structure as any;
    if (formStructure) {
      if (formStructure.hide_branding === true) {
        return true;
      }
    }

    return false;
  })();

  // Free for now: the saved/theme hide flag alone controls the platform footer.
  const shouldShowBranding = !isBrandingHiddenByConfig;

  // Camera modal state
  const [cameraModalOpen, setCameraModalOpen] = useState(false);
  const [currentCameraField, setCurrentCameraField] = useState<Field | null>(null);
  const [currentPromptId, setCurrentPromptId] = useState<string | undefined>(undefined);
  const webcamRef = useRef<Webcam>(null);

  // Payment modal state
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [currentPaymentField, setCurrentPaymentField] = useState<Field | null>(null);
  const [paymentFieldStatuses, setPaymentFieldStatuses] = useState<Record<string, 'processing' | 'cancelled' | 'failed'>>({});
  // Pre-initialized Paystack data from webhook_call_on_page_submit response
  // `amount` is in MAJOR units and is what the webhook actually bound to the
  // access code. It overrides the field's placeholder amount so the modal shows
  // the figure Paystack will really charge.
  const [preloadedExternalPayment, setPreloadedExternalPayment] = useState<{ accessCode: string; reference: string; amount?: number; sessionId?: string; userId?: string } | null>(null);
  // Subscription data when requires_payment: false (active subscription bypass)
  const [preloadedSubscriptionData, setPreloadedSubscriptionData] = useState<{ subscriptionCode: string; emailToken?: string; customerId?: number; amount?: number; status: string; nextPaymentDate?: string } | null>(null);

  // Section collapse state
  const [sectionCollapsedState, setSectionCollapsedState] = useState<Record<string, boolean>>({});

  // Success state
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Cross-tab conflict detection state
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictData, setConflictData] = useState<{
    localVersion: number;
    storageVersion: number;
    localAnswers: Record<string, any>;
    storageAnswers: Record<string, any>;
  } | null>(null);

  // Track if initial values have been populated to prevent re-initialization on every render
  // Refs
  const lastApiResponseRef = useRef<any>(null);
  const formEntryIdRef = useRef<number | null>(null);
  const initialValuesPopulated = useRef(false);
  const sessionNavigationCompleted = useRef(false); // Track if we've already navigated after session continuation
  const prevFormIdentityRef = useRef<string | number | null>(null); // Track form identity to auto-reset on config change
  const localVersionRef = useRef<number>(0); // Track current version in memory
  const isProcessingStorageEventRef = useRef<boolean>(false); // Prevent infinite loop from storage event processing
  const skipNextDebouncedSaveRef = useRef<boolean>(false); // Skip next debounced save (used after auto-merge)

  // Use form session hook for all session management
  const {
    sessionId,
    userId,
    showSessionModal,
    pendingSessionId,
    sessionDecisionMade,
    userContinuedSession,
    sessionSourceIsBackend,
    tabId,
    setShowSessionModal,
    setSessionDecisionMade,
    handleContinueSession,
    handleStartNewSession,
    initializeSession,
    getFormSessionData,
    validateSessionStaleness,
    updateFormSessionData,
    clearLocalStorageAnswers,
    removeFormSession,
  } = useFormSession({
    formId: formId as string | number,
    propSessionId,
    propUserId,
    sessionModalConfig,
    appConfig: appConfig || undefined,
    applicationId,
    formVersion,
    onFormReset,
    onStartNewSession,
  });

  // Helper functions for page-level localStorage operations
  // These depend on useFormSession hook callbacks, so they're kept inline
  const saveCurrentPageAnswersToLocalStorage = (pageAnswers: Record<string, any>) => {
    // Update local version BEFORE saving to prevent false cross-tab conflicts
    const sessionData = getFormSessionData();
    if (sessionData) {
      localVersionRef.current = (sessionData.version || 0) + 1;
    }

    // Use MERGE mode (default): preserve answers from other pages while updating current page
    updateFormSessionData({
      answers: pageAnswers,
      currentPage: currentPageIndex
    });
  };

  const loadCurrentPageAnswersFromLocalStorage = (): Record<string, any> => {
    const sessionData = getFormSessionData();
    if (sessionData && sessionData.currentPage === currentPageIndex) {
      return sessionData.answers || {};
    }
    return {};
  };

  // Initialize session using the hook
  useEffect(() => {
    // Skip if user already made a session decision to prevent infinite modal loop
    if (sessionDecisionMade) {
      console.log('⏭️ [InitializeSession] Skipping - user already made session decision');
      return;
    }
    initializeSession();
  }, [propSessionId, propUserId, formId, sessionModalConfig.enabled, appConfig?.api_base_url, applicationId, sessionDecisionMade, initializeSession]);

  // Load form on mount or when form identity changes.
  // formConfig?.id (primitive) is safe in deps — the full formConfig object is not.
  useEffect(() => {
    const currentIdentity = formConfig?.id ?? formId;

    // Reset internal state when form identity changes (skip on initial mount)
    if (prevFormIdentityRef.current !== null && prevFormIdentityRef.current !== currentIdentity) {
      setAnswers(new Map());
      setCurrentPageIndex(0);
      setIsSubmitted(false);
      setError("");
      initialValuesPopulated.current = false;
      sessionNavigationCompleted.current = false;
    }

    prevFormIdentityRef.current = currentIdentity;
    loadFormVersion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId, formConfig?.id]);

  // Initialize sections as open (uncollapsed) by default when form loads
  useEffect(() => {
    if (formVersion?.form_structure?.sections) {
      const initialCollapsedState: Record<string, boolean> = {};
      formVersion.form_structure.sections.forEach((section) => {
        initialCollapsedState[section.id] = false; // false = open/uncollapsed
      });
      setSectionCollapsedState(initialCollapsedState);
    }
  }, [formVersion?.form_structure?.sections]);

  // Restore session page and answers when form loads and session is active
  useEffect(() => {
    if (formVersion && sessionId) {
      // Validate session staleness before restoring
      const validation = validateSessionStaleness();

      // If form was already completed, don't restore
      if (!validation.isValid && validation.staleReason?.includes("already submitted")) {
        console.log('⚠️ Form already completed, not restoring localStorage');
        toast.info('This form has already been submitted');
        clearLocalStorageAnswers();
        return;
      }

      // Show warnings if data is stale but still valid
      if (validation.warnings.length > 0) {
        validation.warnings.forEach(warning => {
          console.warn(`⚠️ Session staleness warning: ${warning}`);
          toast.warning(warning, { autoClose: 5000 });
        });
      }

      const sessionData = validation.sessionData;

      // Restore answers from localStorage
      if (sessionData?.answers && Object.keys(sessionData.answers).length > 0) {
        console.log('📝 Restoring answers from localStorage');
        const answersMap = new Map(
          Object.entries(sessionData.answers).map(([k, v]) => [k, extractAnswerValue(v)])
        );
        setAnswers(answersMap);
      }

      // Restore page - but only on initial load before modal decision
      // After user decides (sessionDecisionMade = true), let fetchSessionMetadata handle it
      if (!sessionDecisionMade && sessionData?.currentPage !== undefined && sessionData.currentPage > 0) {
        console.log('📄 Restoring page from localStorage on initial load:', sessionData.currentPage);
        setCurrentPageIndex(sessionData.currentPage);
      }
    }
  }, [formVersion, sessionId, validateSessionStaleness, clearLocalStorageAnswers, sessionDecisionMade]);

  // Cross-tab synchronization: Listen for changes from other tabs
  useEffect(() => {
    if (typeof window === 'undefined' || !formId || !sessionId) return;

    // Initialize local version ref once when component mounts
    const sessionData = getFormSessionData();
    if (sessionData?.version && localVersionRef.current === 0) {
      localVersionRef.current = sessionData.version;
      console.log(`🔢 [Cross-Tab] Initialized localVersionRef to v${sessionData.version}`);
    }

    const handleStorageChange = (e: StorageEvent) => {
      // Only listen to our form's session changes
      if (e.key !== 'form_session_id') return;

      // Prevent infinite loop: ignore events triggered by our own updates
      if (isProcessingStorageEventRef.current) {
        console.log('⏭️  [Cross-Tab] Skipping storage event (already processing)');
        return;
      }

      try {
        const newSessions = e.newValue ? JSON.parse(e.newValue) : {};
        const sessionKey = `form_${formId}`;
        const newSessionData = newSessions[sessionKey];

        if (!newSessionData || typeof newSessionData !== 'object') return;

        // Debug: Log tab IDs
        console.log('🔍 [Cross-Tab] Tab ID check:', {
          storageTabId: newSessionData.tabId,
          currentTabId: tabId,
          match: newSessionData.tabId === tabId,
          storageVersion: newSessionData.version,
          localVersion: localVersionRef.current
        });

        // Ignore events from this same tab (safety check in case browser fires storage events in same tab)
        if (newSessionData.tabId && newSessionData.tabId === tabId) {
          console.log('⏭️  [Cross-Tab] Skipping storage event from same tab');
          return;
        }

        // Check if version has changed
        const storageVersion = newSessionData.version || 0;
        const localVersion = localVersionRef.current;

        console.log(`🔍 [Cross-Tab] Version check: storage v${storageVersion} vs local v${localVersion}`);

        if (storageVersion > localVersion) {
          console.log(`🔄 [Cross-Tab] Detected update from another tab (v${localVersion} → v${storageVersion})`);

          // Get current local answers
          const localAnswersRecord: Record<string, any> = {};
          answers.forEach((value, key) => {
            localAnswersRecord[key] = value;
          });

          const storageAnswers = newSessionData.answers || {};

          // Check for conflicts (both tabs have different values for same field)
          const conflicts: string[] = [];

          // Check if local tab has any answers (if not, it's a fresh tab, just auto-merge)
          const localHasAnswers = Object.keys(localAnswersRecord).length > 0;

          if (localHasAnswers) {
            // Check for fields where local and storage have different values
            Object.keys(localAnswersRecord).forEach(fieldId => {
              if (storageAnswers[fieldId] !== undefined &&
                localAnswersRecord[fieldId] !== storageAnswers[fieldId]) {
                conflicts.push(fieldId);
              }
            });
          }

          if (conflicts.length > 0) {
            console.warn(`⚠️ [Cross-Tab] Detected ${conflicts.length} conflict(s):`, conflicts);

            // Show conflict modal
            setConflictData({
              localVersion,
              storageVersion,
              localAnswers: localAnswersRecord,
              storageAnswers,
            });
            setShowConflictModal(true);
          } else {
            // No conflicts - check if data actually changed before merging
            let hasChanges = false;
            const changedFields: string[] = [];

            // Check if storage has different or new fields
            console.log(`🔍 [Cross-Tab] Checking storage answers:`, Object.keys(storageAnswers));
            console.log(`🔍 [Cross-Tab] Current local answers:`, Array.from(answers.keys()));

            for (const [fieldId, value] of Object.entries(storageAnswers)) {
              const currentValue = answers.get(fieldId);
              if (currentValue !== value) {
                hasChanges = true;
                changedFields.push(fieldId);
                console.log(`🔍 [Cross-Tab] Field changed: ${fieldId}`, {
                  local: currentValue,
                  storage: value,
                  localType: typeof currentValue,
                  storageType: typeof value,
                  isNewField: currentValue === undefined
                });
              }
            }

            // Check if local has fields not in storage
            let localHasExtraFields = false;
            const localOnlyFields: string[] = [];

            for (const fieldId of answers.keys()) {
              if (!(fieldId in storageAnswers)) {
                localHasExtraFields = true;
                localOnlyFields.push(fieldId);
                console.log(`🔍 [Cross-Tab] Field only in local: ${fieldId}`);
              }
            }

            // If local has fields that storage doesn't have, don't auto-merge
            // (that would delete our local changes). Just update version and wait for debounced save.
            if (localHasExtraFields) {
              console.log(`⚠️ [Cross-Tab] Local has ${localOnlyFields.length} extra field(s) not in storage. Skipping auto-merge to preserve local changes.`, localOnlyFields);
              localVersionRef.current = storageVersion;
            } else if (hasChanges) {
              console.log(`✅ [Cross-Tab] Changes detected in ${changedFields.length} field(s), auto-merging...`, changedFields);
              toast.info('Form updated in another tab. Syncing changes...', { autoClose: 3000 });

              const mergedAnswers = new Map(answers);
              Object.entries(storageAnswers).forEach(([fieldId, value]) => {
                // localStorage stores plain values, not wrapped metadata
                // No need to call extractAnswerValue here
                mergedAnswers.set(fieldId, value);
              });

              // Set flags to prevent unwanted side effects (debounced save, session navigation)
              isProcessingStorageEventRef.current = true;
              skipNextDebouncedSaveRef.current = true;
              console.log('🛑 [Auto-Merge] Setting flags to prevent side effects');

              setAnswers(mergedAnswers);

              // Update local version to match storage (don't write back - that would trigger another event)
              localVersionRef.current = storageVersion;
              console.log(`📌 [Auto-Merge] Updated local version to v${localVersionRef.current}`);

              // Reset flags after a short delay
              setTimeout(() => {
                isProcessingStorageEventRef.current = false;
                console.log('✅ [Auto-Merge] Reset isProcessingStorageEventRef');
              }, 100);
            } else {
              // No actual data changes, just update version to prevent repeated checks
              console.log('✅ [Cross-Tab] No data changes, updating version only (v' + localVersion + ' → v' + storageVersion + ')');
              localVersionRef.current = storageVersion;
            }
          }
        }
      } catch (error) {
        console.error('❌ [Cross-Tab] Error handling storage change:', error);
        isProcessingStorageEventRef.current = false; // Reset flag on error
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [formId, sessionId, answers, getFormSessionData]);

  // Session continuation: Navigate to correct page after user continues session
  // This only runs ONCE when session is continued, not on subsequent answer changes
  useEffect(() => {
    // Reset flag when user starts a new session
    if (!userContinuedSession && sessionDecisionMade) {
      sessionNavigationCompleted.current = false;
      return;
    }

    if (!formVersion || !userContinuedSession || !sessionDecisionMade) return;

    // Skip if we've already navigated for this session continuation
    if (sessionNavigationCompleted.current) return;

    // Skip if we're processing a cross-tab storage event (prevents unwanted navigation)
    if (isProcessingStorageEventRef.current || skipNextDebouncedSaveRef.current) {
      console.log('⏭️  [Session Continuation] Skipping navigation during cross-tab sync');
      return;
    }

    // Wait for answers to load before navigating
    if (answers.size === 0) return;

    console.log('🔄 [FormRenderer] Calculating session continuation page...');

    // Convert answers Map to Record (used for condition evaluation)
    const answersRecord: Record<string, any> = Object.fromEntries(answers);

    // Get typed structures for condition evaluation
    const typedPages = formPagesToPages(formVersion.form_structure?.pages || []);
    const typedFields = formFieldsToFields(formVersion.form_structure?.fields || []);

    // Get the page the user was actually on from localStorage
    const sessionData = getFormSessionData();
    const storedCurrentPage = sessionData?.currentPage;

    console.log(`📄 [FormRenderer] Stored current page from localStorage: ${storedCurrentPage}`);

    // Calculate last answered page from formAnswers (for logging/fallback purposes)
    const answerFieldIds = new Set(answers.keys());
    const lastAnsweredPageIndex = calculateLastAnsweredPage(typedPages, typedFields, answerFieldIds, sessionData?.answers);

    console.log(
      `📄 [FormRenderer] Last answered page: ${lastAnsweredPageIndex} ("${typedPages[lastAnsweredPageIndex]?.title}")`
    );

    // Determine target page: prefer the stored current page (where user was working)
    // over the next page after last answered page
    let targetPageIndex: number | null = null;

    if (storedCurrentPage !== undefined && storedCurrentPage >= 0 && storedCurrentPage < typedPages.length) {
      // Navigate to the page they were actually on
      targetPageIndex = storedCurrentPage;
      console.log(
        `✅ [FormRenderer] Navigating to stored current page: ${targetPageIndex} ("${typedPages[targetPageIndex]?.title}")`
      );
    } else {
      // Fallback: Find next visible page after the last answered page
      targetPageIndex = findNextVisiblePage(
        lastAnsweredPageIndex,
        typedPages,
        answersRecord,
        typedFields,
        1 // forward direction
      );

      if (targetPageIndex !== null) {
        console.log(
          `✅ [FormRenderer] Navigating to next visible page: ${lastAnsweredPageIndex} → ${targetPageIndex} ("${typedPages[targetPageIndex]?.title}")`
        );
      }
    }

    if (targetPageIndex !== null) {
      setCurrentPageIndex(targetPageIndex);
      updateFormSessionData({ currentPage: targetPageIndex });
    } else {
      // No next page found - find first visible page from start
      const firstVisiblePage = findNextVisiblePage(-1, typedPages, answersRecord, typedFields, 1);

      if (firstVisiblePage !== null) {
        console.log(`✅ [FormRenderer] No next page, navigating to first visible: ${firstVisiblePage}`);
        setCurrentPageIndex(firstVisiblePage);
        updateFormSessionData({ currentPage: firstVisiblePage });
      } else {
        console.error('❌ [FormRenderer] No visible pages found');
        setCurrentPageIndex(0);
      }
    }

    // Mark navigation as completed to prevent re-running when user types on the form
    sessionNavigationCompleted.current = true;
  }, [formVersion, userContinuedSession, sessionDecisionMade, answers.size, getFormSessionData]);

  // Detect payment callback from Paystack redirect and restore payment modal state
  useEffect(() => {
    if (typeof window === 'undefined' || !formVersion) return;

    const urlParams = new URLSearchParams(window.location.search);
    const trxref = urlParams.get('trxref');
    const reference = urlParams.get('reference');

    if (trxref && reference) {
      console.log('🔄 [FormRenderer] Detected Paystack callback, attempting to restore PaymentModal...');

      // Retrieve payment context from sessionStorage
      const paymentContextStr = sessionStorage.getItem('paymentContext');
      if (paymentContextStr) {
        try {
          const paymentContext = JSON.parse(paymentContextStr);
          const { fieldId } = paymentContext;

          // Cast to any/Field because FormField from form-types and Field from types are mismatched
          // but at runtime the object structure is compatible for PaymentModal
          const field = formVersion.form_structure.fields.find(f => f.id === fieldId) as unknown as Field;
          if (field) {
            console.log('✅ [FormRenderer] Restoring payment state for field:', fieldId);
            setCurrentPaymentField(field);
            setPaymentModalOpen(true);
            // Do NOT clear URL params or session storage here.
            // PaymentModal will handle verification and cleanup.
          } else {
            console.warn('⚠️ [FormRenderer] Could not find field for payment context:', fieldId);
          }
        } catch (err) {
          console.error('❌ [FormRenderer] Failed to parse payment context:', err);
        }
      }
    }
  }, [formVersion]); // Run when formVersion is loaded


  // Populate initial values and initialize boolean radio fields to false
  useEffect(() => {
    // Skip if formVersion not loaded yet
    if (!formVersion) {
      console.log('[InitialValues] Skipping - no formVersion yet');
      return;
    }

    const hasInitialValues = initialValues && Object.keys(initialValues).length > 0;
    const hasFormAnswers = formAnswers && Object.keys(formAnswers).length > 0;
    const searchString = typeof window !== 'undefined' ? getEmbedSearchString() : '';
    const urlParams = autoPopulateFromUrl && searchString ? getFilteredParams(searchString) : {};
    const hasUrlParams = Object.keys(urlParams).length > 0;

    console.log('[FormRenderer] Pre-population Check:', {
      autoPopulateFromUrl,
      urlParams,
      hasUrlParams
    });

    console.log('[InitialValues] Effect triggered:', {
      hasInitialValues,
      hasFormAnswers,
      hasUrlParams,
      autoPopulateFromUrl,
      initialValuesPopulated: initialValuesPopulated.current,
      initialValuesKeys: Object.keys(initialValues || {}),
      formAnswersKeys: Object.keys(formAnswers || {}),
      urlParamKeys: Object.keys(urlParams)
    });

    // Only populate once, but allow updates if new initialValues or formAnswers come in
    if (initialValuesPopulated.current && !hasInitialValues && !hasFormAnswers && !hasUrlParams) {
      console.log('[InitialValues] Skipping - already populated and no new values');
      return;
    }

    setAnswers((prevAnswers) => {
      const newAnswers = new Map<string, any>();

      // If we've already populated once, preserve existing user input
      if (initialValuesPopulated.current) {
        prevAnswers.forEach((value, key) => {
          newAnswers.set(key, value);
        });
      }

      // First, initialize all boolean/radio fields to false if not already set (only on first run)
      if (!initialValuesPopulated.current && formVersion.form_structure?.fields) {
        const booleanRadioFields = formVersion.form_structure.fields.filter(
          (field: FormField) => field.type === "radio" && (
            field.fieldType === "boolean" ||
            field.options?.some((opt: any) => opt.value === "true" || opt.value === "false")
          )
        );

        booleanRadioFields.forEach((field: FormField) => {
          if (!newAnswers.has(field.id)) {
            newAnswers.set(field.id, false);
          }
        });
      }

      // 1. Populate from formAnswers (Highest Priority)
      if (hasFormAnswers) {
        const answersToUse = getAnswersFromFormAnswers(formAnswers);
        console.log('[InitialValues] Populating from formAnswers:', answersToUse);
        Object.entries(answersToUse).forEach(([fieldId, value]) => {
          if (!newAnswers.has(fieldId)) {
            newAnswers.set(fieldId, extractAnswerValue(value));
          }
        });
      }

      // 2. Populate from initialValues (Medium Priority)
      if (hasInitialValues) {
        console.log('[InitialValues] Mapping and populating from initialValues:', initialValues);
        const mappedInitialValues = mapInitialValuesToFields(initialValues, formVersion.form_structure.fields);

        mappedInitialValues.forEach((value, fieldId) => {
          // Only set if not already present (to preserve higher priority formAnswers)
          if (!newAnswers.has(fieldId)) {
            newAnswers.set(fieldId, value);
          }
        });
      }

      // 3. Populate from URL parameters (Lowest Priority)
      if (hasUrlParams) {
        console.log('[InitialValues] Mapping and populating from URL parameters:', urlParams);
        const mappedUrlParams = mapInitialValuesToFields(urlParams, formVersion.form_structure.fields);
        console.log('[InitialValues] Mapped URL parameters:', Object.fromEntries(mappedUrlParams));

        mappedUrlParams.forEach((value, fieldId) => {
          // Only set if not already present (to preserve higher priority formAnswers or initialValues)
          if (!newAnswers.has(fieldId)) {
            newAnswers.set(fieldId, value);
          }
        });
      }

      const cleaned = cleanupHiddenFieldAnswers(newAnswers, formVersion);
      console.log('[InitialValues] Final answers after population:', Object.fromEntries(cleaned.entries()));
      return cleaned;
    });

    // Mark as populated after first successful population
    if (!initialValuesPopulated.current && (hasInitialValues || hasFormAnswers || hasUrlParams)) {
      console.log('[InitialValues] Marking as populated');
      initialValuesPopulated.current = true;
    }
  }, [formVersion]); // ← CHANGED: Removed initialValues and formAnswers

  // Extract backend URL as a primitive to avoid object reference issues
  const backendApiUrl = appConfig?.backend_api_base_url || appConfig?.api_base_url || API_BASE_URL;

  // Auto-fetch saved answers from backend if formAnswers prop is empty
  useEffect(() => {
    const fetchSavedAnswers = async () => {
      // Skip if formAnswers already provided as prop
      if (formAnswers && Object.keys(formAnswers).length > 0) {
        console.log('📝 [FormRenderer] Using provided formAnswers, skipping backend fetch');
        setLoadedFormAnswers(formAnswers);
        return;
      }

      // Skip if no sessionId available
      if (!sessionId) {
        console.log('📝 [FormRenderer] No sessionId available, cannot fetch saved answers');
        return;
      }

      // Skip if user hasn't made a decision yet (modal is showing)
      if (!sessionDecisionMade) {
        console.log('📝 [FormRenderer] Session decision not made yet, skipping fetch');
        return;
      }

      // Skip if user chose to start a new session (no saved answers to fetch)
      if (sessionDecisionMade && !userContinuedSession) {
        console.log('📝 [FormRenderer] User started new session, no saved answers to fetch');
        setLoadedFormAnswers({});
        return;
      }

      // Skip if already fetching
      if (isFetchingAnswers) {
        return;
      }

      try {
        setIsFetchingAnswers(true);
        console.log('📝 [FormRenderer] Fetching saved answers from backend for sessionId:', sessionId);

        // Fetch saved answers
        const response = await getSavedAnswersBySession(sessionId, backendApiUrl);

        console.log('📝 [FormRenderer] Backend response code:', response.code);

        if (response.code === 200 && response.data?.answers) {
          const answerCount = Object.keys(response.data.answers).length;
          console.log(`✅ [FormRenderer] Loaded ${answerCount} saved answer(s) from backend`);
          setLoadedFormAnswers(response.data.answers);
        } else {
          console.log('⚠️ [FormRenderer] No saved answers found, using empty object');
          setLoadedFormAnswers({});
        }
      } catch (error) {
        console.error('❌ [FormRenderer] Error fetching saved answers:', error);
        // Don't block rendering on error - just use empty answers
        setLoadedFormAnswers({});
      } finally {
        setIsFetchingAnswers(false);
      }
    };

    fetchSavedAnswers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, backendApiUrl, sessionDecisionMade, userContinuedSession]);

  // Fetch session metadata and load answers from loadedFormAnswers
  useEffect(() => {
    // Loads loadedFormAnswers into state. When mergeLocalStorage is true, overlays
    // unsaved localStorage answers on top (only used in the backend success path).
    const applyFormAnswers = (prev: Map<string, any>, mergeLocalStorage = false): Map<string, any> => {
      const newAnswers = new Map(prev);
      const answersToUse = getAnswersFromFormAnswers(loadedFormAnswers);
      Object.entries(answersToUse).forEach(([fieldId, value]) => {
        newAnswers.set(fieldId, extractAnswerValue(value));
      });

      if (mergeLocalStorage) {
        const localStorageAnswers = getFormSessionData()?.answers || {};
        Object.entries(localStorageAnswers).forEach(([fieldId, value]) => {
          // localStorage stores plain values, no need to extract
          newAnswers.set(fieldId, value);
        });
      }

      return cleanupHiddenFieldAnswers(newAnswers, formVersion);
    };

    const hasFormAnswers = loadedFormAnswers && Object.keys(loadedFormAnswers).length > 0;

    const fetchSessionMetadata = async () => {
      if (typeof window === 'undefined') return;
      if (!sessionId || !appConfig?.api_base_url) return;

      try {
        const fetchUrl = `${appConfig.api_base_url}/api/form-entries/session?session_id=${sessionId}`;

        const response = await fetch(fetchUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const result = await response.json();
          const data = result.data || result;

          if (data) {
            console.log('✅ Retrieved session metadata from backend:', data);

            // Store form_entry_id for persistAnswers usage
            if (data.form_entry_id) {
              formEntryIdRef.current = data.form_entry_id;
            }

            // Only load loadedFormAnswers if user continued session (not starting fresh)
            if (userContinuedSession && loadedFormAnswers && Object.keys(loadedFormAnswers).length > 0) {
              console.log('📥 Loading answers from loadedFormAnswers:', Object.keys(loadedFormAnswers).length, 'fields');
              setAnswers(prev => {
                const newAnswers = new Map(prev);

                // Load loadedFormAnswers (extract values from metadata objects)
                const answersToUse = getAnswersFromFormAnswers(loadedFormAnswers);
                Object.entries(answersToUse).forEach(([fieldId, value]) => {
                  newAnswers.set(fieldId, extractAnswerValue(value));
                });

                // Override with localStorage answers (current page unsaved work)
                const sessionData = getFormSessionData();
                const localStorageAnswers = sessionData?.answers || {};

                if (Object.keys(localStorageAnswers).length > 0) {
                  console.log('📱 Merging localStorage answers (unsaved work) with loadedFormAnswers');

                  console.log('📱 Applying LocalStorage overrides for unsaved work:', localStorageAnswers);
                  Object.entries(localStorageAnswers).forEach(([fieldId, value]) => {
                    newAnswers.set(fieldId, extractAnswerValue(value));
                  });
                }

                return cleanupHiddenFieldAnswers(newAnswers, formVersion);
              });
            } else if (!userContinuedSession && sessionDecisionMade) {
              console.log('ℹ️ User started new session - skipping loadedFormAnswers load');
            }

            // Note: Page navigation is now handled by the dedicated useEffect below
            // which properly evaluates page visibility based on form conditions and loadedFormAnswers
            if (userContinuedSession && loadedFormAnswers && Object.keys(loadedFormAnswers).length > 0) {
              toast.success('Session resumed');
            }
          }
        } else {
          // Backend fetch failed — still load loadedFormAnswers if user continued session
          if (userContinuedSession && hasFormAnswers) {
            setAnswers(prev => applyFormAnswers(prev));
            toast.success('Session resumed');
          }
        }
      } catch (error: any) {
        console.error('❌ Error fetching session metadata:', error);
        // Fetch threw — still load loadedFormAnswers if user continued session
        if (userContinuedSession && hasFormAnswers) {
          setAnswers(prev => applyFormAnswers(prev));
          toast.success('Session resumed');
        }
      }
    };

    if (formVersion && sessionId && appConfig?.api_base_url && sessionSourceIsBackend) {
      fetchSessionMetadata();
    } else if (formVersion && hasFormAnswers && !sessionSourceIsBackend && userContinuedSession) {
      // Not backend-managed but loadedFormAnswers provided and user continued — load directly
      setAnswers(prev => applyFormAnswers(prev));
    }
  }, [formVersion, sessionId, appConfig?.api_base_url, loadedFormAnswers, sessionSourceIsBackend, userContinuedSession, sessionDecisionMade]);

  // Notify parent of page changes
  useEffect(() => {
    if (onPageChange && formVersion) {
      onPageChange(currentPageIndex);
    }
  }, [currentPageIndex, formVersion]);

  // Load saved answers from local storage when page changes
  // This ONLY applies to auto-managed sessions (no propSessionId provided)
  useEffect(() => {
    if (typeof window === 'undefined' || !formVersion) return;

    // Only load from local storage if session is NOT backend-managed
    // Backend-managed sessions get their answers from formAnswers prop
    if (sessionSourceIsBackend) {
      console.log('Skipping local storage load - backend-managed session uses formAnswers prop');
      return;
    }

    // Load answers from local storage for the current page (unsaved work)
    const savedAnswers = loadCurrentPageAnswersFromLocalStorage();

    if (Object.keys(savedAnswers).length > 0) {
      console.log('Loading saved answers from local storage for page:', currentPageIndex, savedAnswers);

      // Pre-populate the form with saved answers
      setAnswers(prev => {
        const newAnswers = new Map(prev);
        Object.entries(savedAnswers).forEach(([fieldId, value]) => {
          newAnswers.set(fieldId, value);
        });

        const cleanedMap = cleanupHiddenFieldAnswers(newAnswers, formVersion);

        // Sync cleaned answers back to local storage to prevent stale data persistence
        if (cleanedMap.size !== newAnswers.size) {
          const cleanedRecord: Record<string, any> = {};
          cleanedMap.forEach((val: any, key: string) => {
            cleanedRecord[key] = val;
          });

          // Update local version BEFORE saving to prevent false cross-tab conflicts
          const sessionData = getFormSessionData();
          if (sessionData) {
            localVersionRef.current = (sessionData.version || 0) + 1;
          }

          // Use REPLACE mode: allow deletion of removed keys (hidden fields)
          updateFormSessionData({ answers: cleanedRecord }, { mergeAnswers: false });
        }

        return cleanedMap;
      });

      toast.info('Restored unsaved changes');
    }
  }, [currentPageIndex, formVersion, sessionSourceIsBackend]);

  // Save answers to local storage when they change (Debounced)
  useEffect(() => {
    if (typeof window === 'undefined' || !formVersion) return;

    // Logic to save current page answers
    const saveToLocalStorage = () => {
      // Skip this save if it was triggered by auto-merge (prevents ping-pong effect)
      if (skipNextDebouncedSaveRef.current) {
        console.log('⏭️  [Debounced Save] Skipping save after auto-merge');
        skipNextDebouncedSaveRef.current = false;
        return;
      }

      // Get all fields for the current page
      const pages = formVersion?.form_structure?.pages || [];
      const currentPage = pages[currentPageIndex];

      if (currentPage) {
        const currentPageFields = formVersion?.form_structure?.fields?.filter(
          (f: Field) => f.pageId === currentPage.id
        ) || [];
        const currentPageFieldIds = new Set(currentPageFields.map(f => f.id));

        // Create an object with only current page answers
        const currentPageAnswers: Record<string, any> = {};
        let hasAnyValue = false;

        answers.forEach((val: any, key: string) => {
          if (currentPageFieldIds.has(key)) {
            currentPageAnswers[key] = val;
            // Check if value is not empty
            if (val !== '' && val !== null && val !== undefined) {
              hasAnyValue = true;
            }
          }
        });

        // If all inputs are blank, clear the answers in local storage
        // Otherwise, save the current page answers
        if (!hasAnyValue) {
          clearLocalStorageAnswers();
        } else {
          saveCurrentPageAnswersToLocalStorage(currentPageAnswers);
        }
      }
    };

    const timer = setTimeout(saveToLocalStorage, 5000); // 5000ms debounce to reduce cross-tab conflicts

    return () => clearTimeout(timer);
  }, [answers, currentPageIndex, formVersion]);

  // Convert form structure to typed Pages and Fields for condition evaluation (memoized)
  const { typedPages, typedFields } = useMemo(() => {
    if (!formVersion?.form_structure) {
      return { typedPages: [], typedFields: [] };
    }
    return {
      typedPages: formPagesToPages(formVersion.form_structure.pages || []),
      typedFields: formFieldsToFields(formVersion.form_structure.fields || []),
    };
  }, [formVersion?.form_structure?.pages, formVersion?.form_structure?.fields]);

  // Convert answers to record (memoized)
  const answersRecord = useMemo(() => {
    const record: Record<string, any> = {};
    answers.forEach((value, key) => {
      record[key] = value;
    });
    return record;
  }, [answers]);

  // Calculate visible pages (memoized)
  const visiblePages = useMemo(() => {
    return getVisiblePages(typedPages, answersRecord, typedFields);
  }, [typedPages, answersRecord, typedFields]);

  // Check if there's a next visible page
  const hasNextVisiblePage = useMemo(() => {
    return findNextVisiblePage(currentPageIndex, typedPages, answersRecord, typedFields, 1) !== null;
  }, [currentPageIndex, typedPages, answersRecord, typedFields]);

  // Check if there's a previous visible page
  const hasPreviousVisiblePage = useMemo(() => {
    return findNextVisiblePage(currentPageIndex, typedPages, answersRecord, typedFields, -1) !== null;
  }, [currentPageIndex, typedPages, answersRecord, typedFields]);

  // Validate that current page is visible based on loaded answers
  // This handles edge cases where user refreshes browser on a page that should now be hidden
  useEffect(() => {
    if (!formVersion || typedPages.length === 0) return;

    const currentPage = typedPages[currentPageIndex];
    if (!currentPage) return;

    // Check if current page should be visible
    const isCurrentPageVisible = evaluatePageConditions(currentPage, answersRecord, typedFields);

    if (!isCurrentPageVisible) {
      console.warn(
        `[FormRenderer] Current page ${currentPageIndex} (order: ${currentPage.order}) is hidden based on conditions. ` +
        `Finding next visible page...`
      );

      // Find next visible page (forward first, then backward)
      let nextPageIndex = findNextVisiblePage(currentPageIndex, typedPages, answersRecord, typedFields, 1);

      if (nextPageIndex === null) {
        // No forward page found, try backward
        nextPageIndex = findNextVisiblePage(currentPageIndex, typedPages, answersRecord, typedFields, -1);
      }

      if (nextPageIndex !== null) {
        console.log(`[FormRenderer] Navigating to visible page ${nextPageIndex}`);
        setCurrentPageIndex(nextPageIndex);
        updateFormSessionData({ currentPage: nextPageIndex });
      } else {
        // No visible pages found (shouldn't happen if first page has no conditions)
        console.error(
          '[FormRenderer] CRITICAL: No visible pages found - form is in an invalid state. ' +
          'Ensure the first page (order=0) has no conditions.'
        );
      }
    }
  }, [formVersion, currentPageIndex, typedPages, typedFields, answersRecord]);

  const loadFormVersion = async () => {
    setLoading(true);
    setError("");

    console.log('[FormRenderer] loadFormVersion called');
    console.log('[FormRenderer] formConfig:', formConfig);
    console.log('[FormRenderer] hasFormConfig:', !!formConfig);
    console.log('[FormRenderer] formConfig type:', typeof formConfig);
    console.log('[FormRenderer] formConfig keys:', formConfig ? Object.keys(formConfig) : 'null/undefined');



    // If staticFormVersion is provided, use it directly
    if (staticFormVersion) {
      console.log('[FormRenderer] Using static form version');
      // Convert FormField[] to Field[] at API boundary
      const convertedVersion: RuntimeFormVersionDetails = {
        ...staticFormVersion,
        form_structure: {
          ...staticFormVersion.form_structure,
          fields: formFieldsToFields(staticFormVersion.form_structure.fields)
        }
      };
      setFormVersion(convertedVersion);
      setLoading(false);
      return;
    }

    // If formConfig is provided, use it directly (client-provided flattened structure)
    if (formConfig) {
      console.log('[FormRenderer] Using formConfig from client');
      const formVersionData = convertToFormVersionDetails(formConfig, formId || propFormVersionId);
      setFormVersion(formVersionData);
      setLoading(false);
      return;
    }

    // Fallback: fetch from backend (returns same flattened structure)
    try {
      const effectiveFormId = formId || propFormVersionId;
      if (!effectiveFormId) {
        throw new Error('Either formId or formVersionId must be provided');
      }

      // Use backend URL from context, fallback to environment variable
      const backendUrl = appConfig?.backend_api_base_url || appConfig?.api_base_url || API_BASE_URL;
      const fetchUrl = propFormVersionId && !formId
        ? `${backendUrl}/api/v1/form-versions/get-version/${propFormVersionId}`
        : `${backendUrl}/api/v1/forms/get-published-version/${effectiveFormId}`;
      const fetchResponse = await fetch(fetchUrl);

      if (!fetchResponse.ok) {
        throw new Error(`HTTP ${fetchResponse.status}: ${fetchResponse.statusText}`);
      }

      const response = await fetchResponse.json();

      if (response.code === 200 && response.data) {
        console.log('[FormRenderer] Loaded form version from backend');
        const formVersionData = convertToFormVersionDetails(response.data, effectiveFormId);
        setFormVersion(formVersionData);
      } else {
        const errorMsg = response.message || `Form ID ${formId} not found or has no published version`;
        console.error("Form load failed:", errorMsg);
        setError(errorMsg);
        toast.error("Failed to load form");
        onError?.(errorMsg);
      }
    } catch (error: any) {
      console.error("Error loading form:", error);
      const errorMsg = error.message || "Failed to load form";
      setError(errorMsg);
      toast.error("Failed to load form");
      onError?.(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldId: string, value: any) => {
    setAnswers(prev => {
      const newAnswers = new Map(prev);
      newAnswers.set(fieldId, value);

      // Clean up answers for fields that are now hidden due to this change
      const cleanedAnswers = cleanupHiddenFieldAnswers(newAnswers, formVersion);

      // Note: Removed immediate localStorage save to prevent cross-tab sync issues
      // The debounced save useEffect (5 second delay) will handle saving to localStorage

      return cleanedAnswers;
    });
  };

  const openCameraModal = (field: Field, promptId?: string) => {
    if (readOnly) return;
    setCurrentCameraField(field);
    setCurrentPromptId(promptId);
    setCameraModalOpen(true);
  };

  const handleCameraCapture = (capturedData: File | File[] | CapturedImage[]) => {
    if (!currentCameraField) return;

    handleFieldChange(currentCameraField.id, capturedData);
    setCameraModalOpen(false);
    setCurrentCameraField(null);
    setCurrentPromptId(undefined);
  };

  // Handle section collapse change
  const handleSectionCollapseChange = (sectionId: string, collapsed: boolean) => {
    setSectionCollapsedState((prev) => ({
      ...prev,
      [sectionId]: collapsed,
    }));
  };

  // Payment modal handlers
  const openPaymentModal = (field: Field) => {
    if (readOnly) return;

    // If subscription data is already loaded, bypass the modal entirely
    if (preloadedSubscriptionData) {
      console.log('🎫 openPaymentModal: active subscription bypass, skipping modal');
      const subData = preloadedSubscriptionData;
      const subResult = {
        success: true,
        requires_payment: false,
        subscription_code: subData.subscriptionCode,
        status: subData.status,
        customer_id: subData.customerId,
        amount: subData.amount,
        next_payment_date: subData.nextPaymentDate,
      };
      lastApiResponseRef.current = subResult;
      onPaymentVerified?.(subResult);

      const finalAnswers = new Map(answersRef.current);
      finalAnswers.set(field.id, subData.subscriptionCode);
      if (formVersion?.form_structure?.fields) {
        for (const hiddenField of formVersion.form_structure.fields.filter((f: Field) => f.type === 'hidden')) {
          const isLinked = (hiddenField as any).linkedPaymentFieldId === field.id;
          const matchByLabel = hiddenField.label === 'Transaction ID' || hiddenField.label === 'Transaction Status';
          const matchByDataKey = (hiddenField as any).dataKey === 'transactionId' || (hiddenField as any).dataKey === 'transactionStatus';
          if (isLinked || matchByLabel || matchByDataKey) {
            if (hiddenField.label === 'Transaction ID' || (hiddenField as any).dataKey === 'transactionId') {
              finalAnswers.set(hiddenField.id, subData.subscriptionCode);
            } else if (hiddenField.label === 'Transaction Status' || (hiddenField as any).dataKey === 'transactionStatus') {
              finalAnswers.set(hiddenField.id, 'completed');
            }
          }
        }
      }
      setAnswers(finalAnswers);
      setPreloadedSubscriptionData(null);
      toast.success('Active subscription verified! You can now submit the form.');
      return;
    }

    // Clear any previous cancelled/failed status so retry starts fresh
    setPaymentFieldStatuses(prev => {
      const next = { ...prev };
      delete next[field.id];
      next[field.id] = 'processing';
      return next;
    });
    setCurrentPaymentField(field);
    setPaymentModalOpen(true);
  };

  const closePaymentModal = (status?: 'cancelled' | 'failed') => {
    if (status && currentPaymentField) {
      setPaymentFieldStatuses(prev => ({ ...prev, [currentPaymentField.id]: status }));
    } else if (currentPaymentField) {
      setPaymentFieldStatuses(prev => {
        const next = { ...prev };
        delete next[currentPaymentField.id];
        return next;
      });
    }
    setPaymentModalOpen(false);
    setCurrentPaymentField(null);
  };

  const handlePaymentSuccess = (transactionId: string, reference: string, provider: string, customerData?: { firstName: string; lastName: string; email: string; phone: string }, verificationResult?: any) => {
    console.log('💳 handlePaymentSuccess called:', { transactionId, reference, provider, hasCurrentPaymentField: !!currentPaymentField, hasCustomerData: !!customerData, hasVerificationResult: !!verificationResult });

    if (verificationResult) {
      lastApiResponseRef.current = verificationResult;
      onPaymentVerified?.(verificationResult);
    }

    if (currentPaymentField) {
      // Build a new map of answers to ensure we have the latest state before completion,
      // avoiding race conditions with setAnswers.
      const finalAnswers = new Map(answersRef.current);

      // 1. Add payment transaction ID to the payment field itself
      finalAnswers.set(currentPaymentField.id, transactionId);

      // 2. Populate hidden fields for Transaction ID & Transaction Status
      if (formVersion?.form_structure?.fields) {
        const hiddenFields = formVersion.form_structure.fields.filter(
          (f: Field) => f.type === 'hidden'
        );
        for (const hiddenField of hiddenFields) {
          const isLinked = (hiddenField as any).linkedPaymentFieldId === currentPaymentField.id;
          const matchByLabel = hiddenField.label === 'Transaction ID' || hiddenField.label === 'Transaction Status';
          const matchByDataKey = (hiddenField as any).dataKey === 'transactionId' || (hiddenField as any).dataKey === 'transactionStatus';

          if (isLinked || matchByLabel || matchByDataKey) {
            if (hiddenField.label === 'Transaction ID' || (hiddenField as any).dataKey === 'transactionId') {
              finalAnswers.set(hiddenField.id, transactionId);
              console.log('💳 Populated hidden field "Transaction ID":', transactionId);
            } else if (hiddenField.label === 'Transaction Status' || (hiddenField as any).dataKey === 'transactionStatus') {
              // For subscription bypass (requires_payment: false), status is the subscription status
              // (e.g. "active") — always force "completed" so the form submission guard passes
              const rawStatus = verificationResult?.requires_payment === false
                ? 'completed'
                : (verificationResult?.transaction?.status || verificationResult?.status || 'completed');

              // Normalize 'success' to 'completed' for internal consistency
              const status = (rawStatus === 'success' || rawStatus === 'successful') ? 'completed' : rawStatus;

              finalAnswers.set(hiddenField.id, status);
              console.log('💳 Populated hidden field "Transaction Status":', status);
            }
          }
        }
      }

      // 3. Auto-fill customer fields if data is returned from payment
      if (customerData && formVersion?.form_structure?.fields) {
        const updatedAnswers = autoFillCustomerFields(formVersion.form_structure.fields, customerData, finalAnswers);
        updatedAnswers.forEach((value, key) => {
          finalAnswers.set(key, value);
        });
      }

      // Update state for UI consistency
      setAnswers(finalAnswers);

      closePaymentModal();

      // Payment successful - answers updated, but user must click "Complete" button
      console.log('💳 Payment successful, transaction details saved. User can now click Complete.');
      toast.success('Payment completed! You can now submit the form.');
    } else {
      console.warn('⚠️ No currentPaymentField set - cannot complete form');
    }
  };

  const handlePaymentError = (error: string) => {
    console.error("Payment error:", error);
    toast.error("Payment failed");
    // Note: Modal close with 'failed' status is handled by PaymentModal's onClose callback
  };

  // Conflict resolution modal handlers
  const handleKeepLocalChanges = () => {
    console.log('✅ [Conflict] User chose to keep current tab data');

    if (conflictData) {
      // Write our local data to storage so other tabs see our choice
      // This will trigger other tabs to detect the conflict
      isProcessingStorageEventRef.current = true;

      // Keep current answers (already in state)
      // Force a write to storage by triggering an update
      const currentAnswersRecord: Record<string, any> = {};
      answers.forEach((value, key) => {
        currentAnswersRecord[key] = value;
      });

      // Update local version BEFORE saving to prevent false cross-tab conflicts
      const sessionData = getFormSessionData();
      if (sessionData) {
        localVersionRef.current = (sessionData.version || 0) + 1;
      }

      // Use REPLACE mode: fully replace storage with current tab's answers
      updateFormSessionData(
        { answers: currentAnswersRecord },
        { mergeAnswers: false }
      );

      // Reset processing flag after a short delay
      setTimeout(() => {
        isProcessingStorageEventRef.current = false;
        console.log(`📌 [Conflict] Reset processing flag after keeping changes`);
      }, 50);
    }

    toast.info('Keeping your current changes');
    setShowConflictModal(false);
    setConflictData(null);
  };

  const handleUseStorageChanges = () => {
    console.log('✅ [Conflict] User chose to use other tab data');
    if (conflictData) {
      isProcessingStorageEventRef.current = true;

      const storageAnswers = new Map();
      Object.entries(conflictData.storageAnswers).forEach(([fieldId, value]) => {
        // localStorage stores plain values, no need to extract
        storageAnswers.set(fieldId, value);
      });
      setAnswers(storageAnswers);

      // Update local version BEFORE saving to prevent false cross-tab conflicts
      const sessionData = getFormSessionData();
      if (sessionData) {
        localVersionRef.current = (sessionData.version || 0) + 1;
      }

      // Use REPLACE mode: fully replace with other tab's answers
      updateFormSessionData(
        { answers: conflictData.storageAnswers },
        { mergeAnswers: false }
      );

      // Reset processing flag after a short delay
      setTimeout(() => {
        isProcessingStorageEventRef.current = false;
        console.log(`📌 [Conflict] Reset processing flag after using storage changes`);
      }, 50);

      toast.success('Using changes from other tab');
    }
    setShowConflictModal(false);
    setConflictData(null);
  };

  const handleSmartMerge = () => {
    console.log('✅ [Conflict] User chose smart merge');
    if (conflictData) {
      isProcessingStorageEventRef.current = true;

      const mergedAnswers = new Map(answers);

      // Merge: keep local values for conflicts, add storage values for non-conflicting fields
      Object.entries(conflictData.storageAnswers).forEach(([fieldId, value]) => {
        // Only add if this field doesn't exist in local answers (no conflict)
        if (!conflictData.localAnswers[fieldId]) {
          // localStorage stores plain values, no need to extract
          mergedAnswers.set(fieldId, value);
        }
      });

      setAnswers(mergedAnswers);

      // Convert merged answers to record and write to storage
      const mergedRecord: Record<string, any> = {};
      mergedAnswers.forEach((value, key) => {
        mergedRecord[key] = value;
      });

      // Update local version BEFORE saving to prevent false cross-tab conflicts
      const sessionDataBeforeSave = getFormSessionData();
      if (sessionDataBeforeSave) {
        localVersionRef.current = (sessionDataBeforeSave.version || 0) + 1;
      }

      // Use REPLACE mode: fully replace with merged result
      updateFormSessionData(
        { answers: mergedRecord },
        { mergeAnswers: false }
      );

      // Reset processing flag after a short delay
      setTimeout(() => {
        isProcessingStorageEventRef.current = false;
        console.log(`📌 [Conflict] Reset processing flag after smart merge`);
      }, 50);

      toast.success('Changes merged successfully');
    }
    setShowConflictModal(false);
    setConflictData(null);
  };

  // Resolve mapped payment values for current payment field
  const mappedValues = getMappedPaymentValues(currentPaymentField, answers);

  // Uploads any image fields on the current page. Returns the full answers
  // record with uploaded URLs substituted in, or null if an upload failed.
  const uploadPageImages = async (currentPageFields: Field[], currentValuesRecord: Record<string, any>): Promise<Record<string, any> | null> => {
    const imageUploadFields = getImageUploadFields(currentPageFields);
    if (imageUploadFields.length === 0) return currentValuesRecord;

    setSubmitting(true);
    try {
      const answersWithUploadedImages = await handleImageUploads(
        imageUploadFields,
        currentValuesRecord,
        async () => {
          const token = await getAccessToken();
          const headers: Record<string, string> = {};
          if (token) {
            headers['Authorization'] = `Bearer ${token}`;
          }
          return headers;
        }
      );

      // Note: We intentionally DO NOT update the answers state with the URLs
      // Keep the File objects in state so the user continues to see their local preview
      // The URLs in answersWithUploadedImages are only used for backend submission

      return answersWithUploadedImages;
    } catch (error) {
      console.error('❌ Image upload failed:', error);
      setSubmitting(false);
      return null;
    }
  };

  // Updates last_page_answered in the backend. Non-fatal: errors are logged
  // but do not propagate to the caller.
  const updateSessionProgress = async (currentPage: FormPage): Promise<void> => {
    try {
      const lastPageAnswered = currentPage.order !== undefined ? currentPage.order : currentPageIndex;

      if (sessionSourceIsBackend && updateSessionProgressEndpointPath && appConfig?.api_base_url) {
        const progressUrl = `${appConfig.api_base_url}${updateSessionProgressEndpointPath}`;
        await postWithAuth(progressUrl, {
          session_id: sessionId,
          last_page_answered: lastPageAnswered
        });
      } else {
        // Use backend URL from context, fallback to environment variable
        const backendUrl = appConfig?.backend_api_base_url || appConfig?.api_base_url || API_BASE_URL;
        const response = await fetch(`${backendUrl}/api/v1/form-entries/session/progress`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            session_id: sessionId,
            last_page_answered: lastPageAnswered
          })
        });

        if (!response.ok) {
          console.warn('⚠️ Failed to update session progress. Status:', response.status);
        }
      }
    } catch (error: any) {
      console.error('⚠️ Error updating session progress (non-fatal):', error);
    }
  };

  // POSTs the current page answers to the custom endpoint, stores any lead
  // context, updates session progress, clears localStorage, and fires the
  // onPageComplete callback. Throws on POST failure so the caller can abort.
  const submitPageToBackend = async (
    answersWithUploadedImages: Record<string, any>,
    currentPage: FormPage,
    allFields: Field[],
    currentPageFields: Field[],
    currentPageFieldIds: Set<string>
  ): Promise<void> => {
    if (!customEndpointPath || !appConfig?.api_base_url) return;

    setSubmitting(true);
    try {
      const pageAnswersRecord = preparePageAnswersPayload(
        new Map(Object.entries(answersWithUploadedImages)),
        currentPageFieldIds,
        allFields,
        answersWithUploadedImages,
        userId
      );

      const finalAnswersRecord = ensureBooleanFieldDefaults(currentPageFields, pageAnswersRecord, answersWithUploadedImages, allFields, userId);

      const customUrl = `${appConfig.api_base_url}${customEndpointPath}`;

      let payload: any = {
        form_id: (formId || propFormVersionId || formVersion?.form?.id)?.toString() || '',
        form_version_id: formVersion?.id || null,
        page_number: currentPage.order || currentPageIndex,
        session_id: sessionId,
        user_id: userId,
        answers: finalAnswersRecord,
        form_completed: !hasNextVisiblePage,
      };

      if (payloadTransformer) {
        payload = payloadTransformer(payload);
      }

      const response = await postWithAuth(customUrl, payload);
      lastApiResponseRef.current = response;

      // Store lead context in sessionStorage if returned (for OTP verification after payment)
      if (response?.lead?.id) {
        sessionStorage.setItem('form_lead_id', response.lead.id);
        const phoneField = findPhoneField(allFields);
        if (phoneField) {
          const phoneValue = answers.get(phoneField.id);
          if (phoneValue) {
            sessionStorage.setItem('form_lead_phone', phoneValue);
          }
        }
      }

      toast.success("Page saved successfully");

      await updateSessionProgress(currentPage);

      clearLocalStorageAnswers();

      if (onPageComplete) {
        try {
          await onPageComplete({
            pageIndex: currentPageIndex,
            pageData: finalAnswersRecord,
            allAnswers: Object.fromEntries(answers.entries())
          });
        } catch (error: any) {
          console.error('Error in onPageComplete callback:', error);
        }
      }
    } catch (error: any) {
      console.error("Error saving page:", error);
      toast.error("Failed to save page");
      throw error;
    } finally {
      setSubmitting(false);
    }
  };

  const handlePageSubmit = async () => {
    if (readOnly) {
      handleNextPage();
      return;
    }

    const pages = formVersion?.form_structure?.pages || [];
    const currentPage = pages[currentPageIndex];

    if (!currentPage) {
      handleNextPage();
      return;
    }

    setSubmitting(true);

    const allFields = formVersion?.form_structure?.fields || [];
    const currentPageFields = allFields.filter((f: Field) => f.pageId === currentPage.id);
    const currentPageFieldIds = getPageFieldIds(formVersion, currentPage.id);
    const currentValuesRecord = Object.fromEntries(answers.entries());

    // Step 0: Validate required fields on the current page
    // Only validate visible, non-hidden fields that are marked as required
    const visibleRequiredFields = currentPageFields.filter((f: Field) => {
      // Skip hidden fields
      if (f.type === 'hidden') return false;
      // Skip non-required fields
      if (!f.required) return false;
      // Skip fields hidden by conditions
      if (f.conditions && f.conditions.length > 0) {
        if (!evaluateFieldConditions(f, currentValuesRecord, currentPageFields)) return false;
      }
      return true;
    });

    // Optional fields with a validation pattern are also validated when they have a value.
    // validateField already skips pattern checks for empty values, but we filter to non-empty
    // values here to keep the loop tight and avoid re-checking empty optional fields.
    const visibleOptionalPatternFields = currentPageFields.filter((f: Field) => {
      if (f.type === 'hidden') return false;
      if (f.required) return false;
      if (!f.validation?.pattern) return false;
      if (f.conditions && f.conditions.length > 0) {
        if (!evaluateFieldConditions(f, currentValuesRecord, currentPageFields)) return false;
      }
      const v = answers.get(f.id);
      const sv = v === null || v === undefined ? '' : String(v).trim();
      return sv.length > 0;
    });

    const invalidRequiredFields: string[] = [];
    for (const field of visibleRequiredFields) {
      const value = answers.get(field.id);
      const result = validateField(field, value);
      if (!result.isValid) {
        invalidRequiredFields.push(field.label || field.id);
      }
    }

    const invalidOptionalFields: string[] = [];
    for (const field of visibleOptionalPatternFields) {
      const value = answers.get(field.id);
      const result = validateField(field, value);
      if (!result.isValid) {
        invalidOptionalFields.push(field.label || field.id);
      }
    }

    if (invalidRequiredFields.length > 0 || invalidOptionalFields.length > 0) {
      if (invalidOptionalFields.length > 0) {
        // Combine required + optional failures into one message; required wording is preserved
        // when only required fields fail (handled by the else branch below).
        const allInvalid = [...invalidRequiredFields, ...invalidOptionalFields];
        toast.error(`Please correct the following fields: ${allInvalid.join(', ')}`);
      } else {
        const fieldList = invalidRequiredFields.join(', ');
        toast.error(`Please fill in all required fields: ${fieldList}`);
      }
      setSubmitting(false);
      return; // Block navigation — user stays on current page
    }

    // Step 1: upload any images on this page
    const answersWithUploadedImages = await uploadPageImages(currentPageFields, currentValuesRecord);
    if (answersWithUploadedImages === null) {
      setSubmitting(false);
      return;
    }

    // Step 2: POST page to backend (no-op if no custom endpoint configured)
    try {
      await submitPageToBackend(answersWithUploadedImages, currentPage, allFields, currentPageFields, currentPageFieldIds);
    } catch {
      setSubmitting(false);
      return; // submission failed — don't navigate
    }

    // Step 2b: Pre-initialize external payment if applicable.
    // Triggered when:
    //   a) the form-level webhook_call_on_page_submit flag is set (call on every page), OR
    //   b) this is the last page and it has an external payment field (is_payment_form flow), OR
    //   c) the NEXT page contains the external payment field (pre-initialize just before user sees it)
    // This handles formConfig/local-config setups where the flag isn't persisted on the object.
    const externalPaymentField = typedFields.find((f) => f.type === 'payment' && f.paymentMode === 'external' && f.customPaymentEndpoint);
    const nextVisiblePageIndex = externalPaymentField
      ? findNextVisiblePage(currentPageIndex, typedPages, answersRecord, typedFields, 1)
      : null;
    const nextPage = nextVisiblePageIndex !== null ? typedPages[nextVisiblePageIndex] : null;
    const isNavigatingToPaymentPage = !!(nextPage && typedFields.some((f) =>
      f.pageId === nextPage.id && f.type === 'payment' && f.paymentMode === 'external' && f.customPaymentEndpoint
    ));
    const isLastPageBeforePayment = !hasNextVisiblePage && !!externalPaymentField;

    // (c) `isNavigatingToPaymentPage` pre-initializes from the page BEFORE the
    // payment page, and the payload is built from `currentPageFieldIds` — so it
    // carries THAT page's answers and nothing the payment page itself asks for.
    //
    // THE INVARIANT THIS IMPOSES ON FORM DESIGN: any field that affects the
    // amount must live on a page before the payment page, never on it. The
    // amount is bound to the Paystack transaction at initialize, and initialize
    // happens here.
    //
    // Observed on staging 2026-08-13 when that invariant was violated: the
    // billing-date dropdown sat on the payment page, so the transaction was
    // initialized before the member's choice existed. The server priced against
    // a fallback and charged R309.29 where the member's selection was worth
    // R489.71 — taken and receipted before anything downstream could notice.
    // The fix was to move the dropdown to the preceding page, not to drop this
    // trigger: dropping it sends initialization down PaymentModal's
    // `handleExternalPayment` fallback, which POSTs a synthetic answers object
    // (email/amount/name only) and carries no form field answers at all — so the
    // dropdown would not reach the server from the payment page either way.
    const webhookPaymentField = (formVersion?.webhook_call_on_page_submit || isLastPageBeforePayment || isNavigatingToPaymentPage)
      ? externalPaymentField
      : null;
    console.log('🔍 webhook_call_on_page_submit:', formVersion?.webhook_call_on_page_submit, '| isLastPageBeforePayment:', isLastPageBeforePayment, '| isNavigatingToPaymentPage:', isNavigatingToPaymentPage, '| webhookPaymentField found:', !!webhookPaymentField);
    // Local variable so the bypass check below can use it synchronously (React setState is async)
    let localSubscriptionData: typeof preloadedSubscriptionData = null;
    if (webhookPaymentField?.customPaymentEndpoint) {
      try {
        const pageAnswers = preparePageAnswersPayload(
          new Map(Object.entries(answersWithUploadedImages)),
          currentPageFieldIds,
          allFields,
          answersWithUploadedImages,
          userId
        );
        const planCode = webhookPaymentField.planConfig?.planCode || webhookPaymentField.amountConfig?.planCode;
        const webhookPayload = {
          form_id: (formId || propFormVersionId || formVersion?.form?.id)?.toString() || '',
          form_version_id: formVersion?.id || null,
          page_number: currentPage.order || currentPageIndex,
          session_id: sessionId,
          user_id: userId,
          answers: pageAnswers,
          form_completed: !hasNextVisiblePage,
          source: 'page_submission',
          ...(planCode ? { plan_code: planCode, planCode } : {}),
        };
        console.log('📡 Calling external webhook on page submission:', webhookPaymentField.customPaymentEndpoint);
        const webhookResponse = await fetch(webhookPaymentField.customPaymentEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(webhookPayload),
        });
        console.log('✅ External webhook page submission call completed');
        // Capture access_code from the response to avoid a second webhook call when the payment modal opens
        if (webhookResponse.ok) {
          try {
            const raw = await webhookResponse.json();
            // n8n may return a single object or wrap it in an array — handle both
            const webhookResult = Array.isArray(raw) ? raw[0] : raw;
            console.log('🔍 Webhook response parsed:', { requires_payment: webhookResult?.requires_payment, has_access_code: !!webhookResult?.access_code, has_reference: !!webhookResult?.reference });
            if (webhookResult?.requires_payment === false && webhookResult?.success) {
              console.log('🎫 Active subscription detected — payment not required, storing subscription data');
              localSubscriptionData = {
                subscriptionCode: webhookResult.subscription_code,
                emailToken: webhookResult.email_token,
                customerId: webhookResult.customer_id,
                amount: webhookResult.amount,
                status: webhookResult.status || 'active',
                nextPaymentDate: webhookResult.next_payment_date,
              };
              setPreloadedSubscriptionData(localSubscriptionData);
            } else if (webhookResult?.requires_payment && webhookResult.access_code && webhookResult.reference) {
              console.log('💳 Pre-initialized Paystack access_code captured from webhook response', { amount: webhookResult.amount });
              setPreloadedExternalPayment({
                accessCode: webhookResult.access_code,
                reference: webhookResult.reference,
                // Captured HERE, on page submission, which is the only point
                // early enough to matter: the payment modal renders its amount
                // as soon as it opens, so a figure resolved later would arrive
                // after the user has already read the wrong one.
                amount: typeof webhookResult.amount === 'number' ? webhookResult.amount : undefined,
                sessionId,
                userId,
              });
            }
          } catch {
            // Non-JSON response is fine — just means no pre-initialized payment data
          }
        }
      } catch (webhookError) {
        console.warn('⚠️ External webhook page submission call failed (non-fatal):', webhookError);
        // Don't block form navigation if webhook fails
      }
    }

    // STEP 2: Handle form submission to custom endpoint (if configured)
    if (customEndpointPath && appConfig?.api_base_url) {
      try {
        // Prepare page answers with visibility checks (using updated answers with image URLs)
        const answersRecord = preparePageAnswersPayload(
          new Map(Object.entries(answersWithUploadedImages)),
          currentPageFieldIds,
          allFields,
          answersWithUploadedImages,
          userId
        );

        // Ensure all boolean/radio/yes-no-toggle fields on current page are included with default false value
        let finalAnswersRecord = ensureBooleanFieldDefaults(currentPageFields, answersRecord, answersWithUploadedImages, allFields, userId);

        console.log('Saving answers for page:', currentPage.order, finalAnswersRecord);

        // Build custom URL and send page answers to custom endpoint
        const customUrl = `${appConfig.api_base_url}${customEndpointPath}`;
        console.log('Sending page answers to custom endpoint:', customUrl);

        // Determine if this is the final page
        const isLastPage = !hasNextVisiblePage;

        // Prepare payload for custom endpoint (per-page submission)
        let payload: any = {
          form_id: (formId || propFormVersionId || formVersion?.form?.id)?.toString() || '',
          form_version_id: formVersion?.id || null,
          page_number: currentPage.order || currentPageIndex,
          session_id: sessionId,
          user_id: userId,
          answers: finalAnswersRecord,
          form_completed: isLastPage,  // TRUE only on the last page
        };

        // Apply payload transformer if provided
        if (payloadTransformer) {
          console.log('Applying payloadTransformer to payload');
          payload = payloadTransformer(payload);
          console.log('Payload after transformation:', payload);
        }

        console.log('Sending payload to custom endpoint:', payload);

        // Send to custom endpoint
        const response = await postWithAuth(customUrl, payload);
        lastApiResponseRef.current = response; // Store response in ref (synchronous)
        console.log('✅ Backend response received and stored:', response);

        // Store lead_id in sessionStorage if returned (for OTP verification after payment)
        if (response && response.lead && response.lead.id) {
          console.log('💾 Storing lead_id in sessionStorage:', response.lead.id);
          sessionStorage.setItem('form_lead_id', response.lead.id);

          // Also store phone number if we can find it in the form answers
          const phoneField = findPhoneField(allFields);
          if (phoneField) {
            const phoneValue = answers.get(phoneField.id);
            if (phoneValue) {
              console.log('💾 Storing phone number in sessionStorage:', phoneValue);
              sessionStorage.setItem('form_lead_phone', phoneValue);
            }
          }
        }

        toast.success("Page saved successfully");

        // Persist to Form Builder DB if alsoSaveToFormBuilder is enabled (also updates progress)
        if (alsoSaveToFormBuilder && formVersion?.id) {
          const cacheKey = `${sessionId}_${formId}_page${currentPage.order}`;

          // Cache locally first for resilience
          cacheSubmission(cacheKey, payload, {
            sessionId: sessionId || '',
            formId: formId || '',
            pageNumber: currentPage.order,
          });

          try {
            // Send page answers using session_id (backend will find/create form_entry)
            console.log('[saveToFormBuilder] Saving page answers with session_id:', sessionId);

            const backendUrl = appConfig?.backend_api_base_url || appConfig?.api_base_url || API_BASE_URL;
            await persistFormAnswers({
              session_id: sessionId,
              application_id: applicationId || '',
              page_number: currentPage.order || currentPageIndex,
              answers: finalAnswersRecord,
              mark_as_complete: !hasNextVisiblePage,
            }, backendUrl);

            // Remove from cache on success
            removeCachedSubmission(cacheKey);
          } catch (persistError) {
            console.warn('[saveToFormBuilder] Failed to persist answers to form builder backend:', persistError);
            // Cache remains for recovery — don't block the form flow
          }
        } else {
          // alsoSaveToFormBuilder is false - only track progress without saving answers
          try {
            console.log('📊 Tracking session progress (no answer persistence)');
            const lastPageAnswered = currentPage.order !== undefined ? currentPage.order : currentPageIndex;

            // For backend-managed sessions, use the provided endpoint
            if (sessionSourceIsBackend && updateSessionProgressEndpointPath && appConfig?.api_base_url) {
              const progressUrl = `${appConfig.api_base_url}${updateSessionProgressEndpointPath}`;
              const progressPayload = {
                session_id: sessionId,
                last_page_answered: lastPageAnswered
              };
              await postWithAuth(progressUrl, progressPayload);
              console.log('✅ Session progress updated successfully');
            }
            // For auto-managed sessions, update using form_entry_id or session_id
            else if (formEntryIdRef.current || sessionId) {
              const backendUrl = appConfig?.backend_api_base_url || appConfig?.api_base_url || API_BASE_URL;
              const updateUrl = `${backendUrl}/api/v1/form-entries/session/progress`;
              const progressBody: Record<string, any> = {
                last_page_answered: lastPageAnswered
              };
              if (formEntryIdRef.current) {
                progressBody.form_entry_id = formEntryIdRef.current;
              } else {
                progressBody.session_id = sessionId;
              }
              const response = await fetch(updateUrl, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(progressBody)
              });

              if (response.ok) {
                console.log('✅ Form entry session progress updated successfully');
              } else {
                console.warn('⚠️ Failed to update form entry session progress. Status:', response.status);
              }
            }
          } catch (progressError: any) {
            console.error('⚠️ Error updating session progress (non-fatal):', progressError);
            // Don't fail the page submission if progress update fails
          }
        }

        // Clear local storage answers after successful submission
        clearLocalStorageAnswers();
        console.log('Cleared local storage answers after page submission');

        // Call onPageComplete callback if provided
        if (onPageComplete) {
          try {
            await onPageComplete({
              pageIndex: currentPageIndex,
              pageData: finalAnswersRecord,
              allAnswers: Object.fromEntries(answers.entries())
            });
          } catch (error: any) {
            console.error('Error in onPageComplete callback:', error);
          }
        }
      } catch (error: any) {
        console.error("Error saving page:", error);
        toast.error("Failed to save page");
        setSubmitting(false);
        return; // Don't proceed to next page if save failed
      } finally {
        setSubmitting(false);
      }
    } else {
      // No customEndpointPath - default to saving to Form Builder DB
      if (formVersion?.id) {
        const cacheKey = `${sessionId}_${formId}_page${currentPage.order}`;

        try {
          // Prepare page answers
          const answersRecord = preparePageAnswersPayload(
            new Map(Object.entries(answersWithUploadedImages)),
            currentPageFieldIds,
            allFields,
            answersWithUploadedImages,
            userId
          );

          const finalAnswersRecord = ensureBooleanFieldDefaults(currentPageFields, answersRecord, answersWithUploadedImages, allFields, userId);

          // Cache locally first for resilience
          cacheSubmission(cacheKey, {
            form_id: (formId || propFormVersionId || formVersion?.form?.id)?.toString() || '',
            form_version_id: formVersion?.id,
            page_number: currentPage.order || currentPageIndex,
            session_id: sessionId,
            answers: finalAnswersRecord,
          }, {
            sessionId: sessionId || '',
            formId: formId || '',
            pageNumber: currentPage.order,
          });

          // Save to Form Builder DB using session_id (backend will find/create form_entry automatically)
          console.log('[defaultFlow] Saving page answers with session_id:', sessionId);

          const backendUrl = appConfig?.backend_api_base_url || appConfig?.api_base_url || API_BASE_URL;
          await persistFormAnswers({
            session_id: sessionId || '',
            application_id: applicationId || '',
            page_number: currentPage.order || currentPageIndex,
            answers: finalAnswersRecord,
            mark_as_complete: !hasNextVisiblePage,
          }, backendUrl);

          // Remove from cache on success
          removeCachedSubmission(cacheKey);
          toast.success("Page saved successfully");

          // Note: Progress is already updated by /save-answers endpoint

          // Clear local storage answers after successful submission
          clearLocalStorageAnswers();
          console.log('Cleared local storage answers after page submission');

          // Call onPageComplete callback if provided
          if (onPageComplete) {
            try {
              await onPageComplete({
                pageIndex: currentPageIndex,
                pageData: finalAnswersRecord,
                allAnswers: Object.fromEntries(answers.entries())
              });
            } catch (error: any) {
              console.error('Error in onPageComplete callback:', error);
            }
          }
        } catch (error: any) {
          console.error("Error saving page to Form Builder DB:", error);
          toast.error("Failed to save page");
          setSubmitting(false);
          return;
        } finally {
          setSubmitting(false);
        }
      }
    }

    // Navigate to next page or complete form
    setSubmitting(false);
    if (hasNextVisiblePage) {
      handleNextPage();
      return;
    }

    // Last page — open payment modal if applicable, otherwise complete
    if (formVersion?.is_payment_form) {
      const paymentField = typedFields.find((f) => f.type === 'payment');

      // Check if payment is already completed to prevent double popup
      const statusField = typedFields.find(
        (f) => f.type === 'hidden' && (f.label === 'Transaction Status' || (f as any).dataKey === 'transactionStatus')
      );
      const statusValue = statusField ? answersRecord[statusField.id] : null;
      const isAlreadyPaid = statusValue === 'completed' || statusValue === 'success' || statusValue === 'successful';

      if (paymentField && evaluateFieldConditions(paymentField, answersRecord, typedFields) && !isAlreadyPaid) {
        // If user has an active subscription, skip the payment modal entirely.
        // Check localSubscriptionData first (same-page webhook response, since setState is async),
        // then fall back to preloadedSubscriptionData (set during a previous page's webhook response).
        const activeSubscription = localSubscriptionData || preloadedSubscriptionData;
        if (activeSubscription) {
          console.log('🎫 Active subscription bypass — skipping payment modal, subscription:', activeSubscription.subscriptionCode);
          const subResult = {
            success: true,
            requires_payment: false,
            subscription_code: activeSubscription.subscriptionCode,
            status: activeSubscription.status,
            customer_id: activeSubscription.customerId,
            amount: activeSubscription.amount,
            next_payment_date: activeSubscription.nextPaymentDate,
          };
          lastApiResponseRef.current = subResult;
          onPaymentVerified?.(subResult);

          // Populate payment field and hidden fields with subscription data
          const finalAnswers = new Map(answersRef.current);
          finalAnswers.set(paymentField.id, activeSubscription.subscriptionCode);
          if (formVersion?.form_structure?.fields) {
            for (const hiddenField of formVersion.form_structure.fields.filter((f: Field) => f.type === 'hidden')) {
              const isLinked = (hiddenField as any).linkedPaymentFieldId === paymentField.id;
              const matchByLabel = hiddenField.label === 'Transaction ID' || hiddenField.label === 'Transaction Status';
              const matchByDataKey = (hiddenField as any).dataKey === 'transactionId' || (hiddenField as any).dataKey === 'transactionStatus';
              if (isLinked || matchByLabel || matchByDataKey) {
                if (hiddenField.label === 'Transaction ID' || (hiddenField as any).dataKey === 'transactionId') {
                  finalAnswers.set(hiddenField.id, activeSubscription.subscriptionCode);
                } else if (hiddenField.label === 'Transaction Status' || (hiddenField as any).dataKey === 'transactionStatus') {
                  finalAnswers.set(hiddenField.id, 'completed');
                }
              }
            }
          }

          setPreloadedSubscriptionData(null);
          toast.success('Active subscription verified! Submitting form...');
          handleFormComplete(finalAnswers);
          return;
        }
        openPaymentModal(paymentField);
        setSubmitting(false);
        return;
      }
    }

    // Block form completion if payment exists but hasn't been completed
    const allFormFields = formVersion?.form_structure?.fields || [];
    const hasPaymentField = allFormFields.some((f: Field) => f.type === 'payment');
    if (hasPaymentField) {
      const statusField = allFormFields.find(
        (f: Field) => f.type === 'hidden' && (f.label === 'Transaction Status' || (f as any).dataKey === 'transactionStatus')
      );
      const statusValue = statusField ? answersRecord[statusField.id] : null;
      const isPaid = statusValue === 'completed' || statusValue === 'success' || statusValue === 'successful';
      if (statusField && !isPaid) {
        console.warn('⚠️ Cannot complete form — payment not yet completed');
        toast.warning('Please complete payment before submitting the form.');
        setSubmitting(false);
        return;
      }
    }

    handleFormComplete();
  };

  const handleNextPage = () => {
    if (typedPages.length === 0) {
      console.error('[FormRenderer] No pages available');
      return;
    }

    // Find next visible page
    const nextPageIndex = findNextVisiblePage(currentPageIndex, typedPages, answersRecord, typedFields, 1);

    if (nextPageIndex !== null) {
      setCurrentPageIndex(nextPageIndex);
      // Update current page in local storage
      updateFormSessionData({ currentPage: nextPageIndex });
    } else {
      // No more visible pages - form is complete
      console.log('[FormRenderer] No more visible pages, completing form');
      handleFormComplete();
    }
  };

  const handlePreviousPage = () => {
    if (typedPages.length === 0) {
      console.error('[FormRenderer] No pages available');
      return;
    }

    // Find previous visible page
    const prevPageIndex = findNextVisiblePage(currentPageIndex, typedPages, answersRecord, typedFields, -1);

    if (prevPageIndex !== null) {
      setCurrentPageIndex(prevPageIndex);
      // Update current page in local storage
      updateFormSessionData({ currentPage: prevPageIndex });
    } else {
      console.warn('[FormRenderer] No previous visible page available');
    }
  };

  const handleFormComplete = async (finalAnswers?: Map<string, any>) => {
    // Use provided final answers or fall back to current state
    const answersToSubmit = finalAnswers || answers;
    const responseToUse = lastApiResponseRef.current; // Get response from ref

    console.log('=== Form Complete ===');
    console.log('Collected answers:', answersToSubmit);
    console.log('API Response from ref:', responseToUse);

    // Update session status to "completed" in form builder backend (if backend-managed session)
    if (sessionSourceIsBackend && updateSessionProgressEndpointPath && appConfig?.api_base_url) {
      try {
        console.log('🏁 Marking session as completed in form builder backend');
        const progressUrl = `${appConfig.api_base_url}${updateSessionProgressEndpointPath}`;

        // Calculate total pages for last_page_answered
        const totalPages = formVersion?.form_structure?.pages?.length || 0;

        const progressPayload = {
          session_id: sessionId,
          last_page_answered: totalPages, // Mark as fully answered
          status: 'completed'
        };

        await postWithAuth(progressUrl, progressPayload);
        console.log('✅ Session marked as completed successfully');
      } catch (completeError: any) {
        console.error('⚠️ Error marking session as completed (non-fatal):', completeError);
      }
    }

    // Convert Map to Record for easier handling
    const answersRecord: Record<string, any> = {};
    answersToSubmit.forEach((value, key) => {
      answersRecord[key] = value;
    });

    toast.success("Form completed successfully!");

    // Mark form as completed in localStorage (prevents restoration if deleteSessionStorage=false)
    updateFormSessionData({
      formCompleted: true,
    });
    console.log('✅ Marked form as completed in localStorage');

    // Store completion state in sessionStorage so it persists across payment redirects
    if (typeof window !== 'undefined') {
      const completionState = {
        completed: true,
        timestamp: Date.now(),
        formId: formId,
      };
      console.log('💾 Saving completion state to sessionStorage:', completionState);
      sessionStorage.setItem('form_completion_state', JSON.stringify(completionState));
      console.log('✅ Completion state saved. Verifying:', sessionStorage.getItem('form_completion_state'));
    }

    // Delete this form's session from localStorage if deleteSessionStorage is true
    if (deleteSessionStorage && typeof window !== 'undefined') {
      removeFormSession();
    }

    // In embedded mode, always show the internal success page within the iframe
    if (embeddedMode) {
      console.log('Embedded form completed - showing success page in iframe');

      // Still notify parent via onComplete if callback provided
      if (onComplete) {
        try {
          setSubmitting(true);
          await onComplete({
            answers: answersRecord,
            formVersion,
            response: responseToUse,
            sessionId: sessionId
          });
          setIsSubmitted(true);
        } catch (err: any) {
          console.error('onComplete callback failed:', err);
          toast.error(err.message || 'Submission failed');
        } finally {
          setSubmitting(false);
        }
      } else {
        setIsSubmitted(true);
      }
      return;
    }

    if (onComplete) {
      try {
        setSubmitting(true);
        await onComplete({
          answers: answersRecord,
          formVersion,
          response: responseToUse, // Use response parameter if provided, otherwise fall back to state
          sessionId: sessionId
        });
      } catch (err: any) {
        console.error('onComplete callback failed:', err);
        toast.error(err.message || 'Submission failed');
      } finally {
        setSubmitting(false);
      }
    } else {
      console.log('No onComplete callback provided - form ended');
      setIsSubmitted(true);
    }
  };

  // Wrappers for session modal handlers from hook
  const handleContinueSessionClick = () => {
    if (pendingSessionId) {
      handleContinueSession(pendingSessionId);
    }
  };

  const handleStartNewSessionClick = () => {
    handleStartNewSession(answers, currentPageIndex, setCurrentPageIndex, setAnswers);
  };


  if (loading) {
    return (
      <UnifiedCard className={className}>
        <UnifiedCardContent>
          <div className="flex items-center justify-center p-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-3 text-muted-foreground">Loading form...</span>
          </div>
        </UnifiedCardContent>
      </UnifiedCard>
    );
  }

  if (error || !formVersion) {
    return (
      <UnifiedCard className={className}>
        <UnifiedCardContent>
          <div className="text-center p-12">
            <div className="text-red-500 mb-4">
              <p className="font-semibold">Failed to load form</p>
              {error && <p className="text-sm mt-2">{error}</p>}
            </div>
            <Button onClick={loadFormVersion} variant="outline">
              Try Again
            </Button>
          </div>
        </UnifiedCardContent>
      </UnifiedCard>
    );
  }

  // Render Success Screen
  if (isSubmitted) {
    const isPaymentForm = formVersion?.is_payment_form;

    return (
      <UnifiedCard className={className}>
        <UnifiedCardContent>
          <div className="text-center p-8 space-y-6">
            <div className="rounded-full bg-green-100 p-3 w-16 h-16 mx-auto flex items-center justify-center">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>

            <h2 className="text-2xl font-bold text-gray-900">
              {isPaymentForm ? 'Payment & Form Complete!' : 'Form Submitted!'}
            </h2>
            <p className="text-gray-600">
              {isPaymentForm
                ? 'Your transaction was successful and your details have been saved.'
                : 'Thank you for your submission.'}
            </p>

            {/* Application Summary - only show for standard forms */}
            {!isPaymentForm && (
              <div className="mt-8 text-left bg-gray-50 rounded-lg p-6 border">
                <h3 className="font-semibold mb-4 text-gray-700">Submission Summary</h3>
                <div className="space-y-3">
                  {Array.from(answers.entries()).map(([key, value]) => {
                    // Try to find a label for this field
                    const field = formVersion.form_structure?.fields?.find(f => f.id === key);
                    const label = field?.label || key;

                    // Skip complex objects or massive text
                    if (typeof value === 'object' && value !== null) return null;
                    if (String(value).length > 100) return null;

                    // Skip hidden fields
                    if (field?.type === 'hidden') return null;

                    return (
                      <div key={key} className="grid grid-cols-3 gap-4 text-sm border-b border-gray-100 pb-2 last:border-0">
                        <span className="font-medium text-gray-500">{label}</span>
                        <span className="col-span-2 text-gray-900 font-medium">{String(value)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {!embeddedMode && (
              <Button onClick={() => window.location.reload()} className="mt-6">
                Start New Form
              </Button>
            )}
          </div>
        </UnifiedCardContent>
      </UnifiedCard>
    );
  }

  const pages = formVersion.form_structure?.pages || [];
  const currentPage = pages[currentPageIndex];
  const fields = formVersion.form_structure?.fields?.filter(
    (f: Field) => f.pageId === currentPage?.id
  ) || [];
  const sections = formVersion.form_structure?.sections || [];

  return (
    <StylingProvider theme={effectiveTheme} chameleonMode={chameleonMode} embeddedMode={embeddedMode}>
      <ThemedRendererRoot className={className} embedded={embeddedMode}>
        <div className="w-full mx-auto" style={{ maxWidth: "var(--form-width, 100%)" }}>
          <UnifiedCard className={`${embeddedMode ? 'border-0 shadow-none' : ''}`}>
            <UnifiedCardContent 
              className={embeddedMode ? 'p-2 sm:p-4' : ''}
            >
              <div className="space-y-6">
              {/* Form Header */}
              {/* Form Header */}
              {!(embeddedMode && effectiveTheme?.config?.header?.hideHeaderEmbedded) && (
                <div
                  className="border-b pb-4 rounded-t-lg transition-all duration-200"
                  style={{
                    backgroundColor: effectiveTheme?.config?.header?.bgColor || 'transparent',
                    padding: effectiveTheme?.config?.header?.bgColor && effectiveTheme?.config?.header?.bgColor !== 'transparent' ? '1.5rem' : '0 0 1rem 0',
                  }}
                >
                  {/* Header layout rendering based on theme config */}
                  {(() => {
                    const headerConfig = effectiveTheme?.config?.header || {};
                    const layout = headerConfig.layout || "top";
                    const defaultAlignByLayout: Record<string, "left" | "center" | "right"> = {
                      top: "center",
                      side: "left",
                      stretch: "center",
                      logo_only: "center",
                    };
                    const align = headerConfig.align || defaultAlignByLayout[layout] || "center";
                    const logo = resolveAssetUrl(effectiveTheme?.assets?.logo);

                    const alignClass =
                      align === "center" ? "items-center text-center" :
                        align === "right" ? "items-end text-right" :
                          "items-start text-left";

                    const textAlignClass =
                      align === "center" ? "text-center" :
                        align === "right" ? "text-right" :
                          "text-left";

                    if (layout === "logo_only") {
                      return (
                        <div className="flex flex-col items-center justify-center p-2">
                          {logo ? (
                            <img src={logo} alt="Logo" className="max-h-20 max-w-[250px] object-contain" />
                          ) : (
                            <div className="text-sm text-muted-foreground italic">No logo selected</div>
                          )}
                        </div>
                      );
                    }

                    if (layout === "side") {
                      return (
                        <div className="flex items-start gap-4">
                          {logo && (
                            <div className="max-h-16 w-16 h-16 flex items-center justify-center shrink-0 border rounded p-1 bg-white">
                              <img src={logo} alt="Logo" className="max-h-full max-w-full object-contain" />
                            </div>
                          )}
                          <div className={`flex-1 min-w-0 ${textAlignClass}`}>
                            <h2 className="text-2xl font-bold">{formVersion.name || 'Form'}</h2>
                            {formVersion.description && (
                              <p className="text-sm text-muted-foreground mt-1">{formVersion.description}</p>
                            )}
                          </div>
                        </div>
                      );
                    }

                    if (layout === "stretch") {
                      return (
                        <div className="flex flex-col gap-3">
                          {logo && (
                            <div className="w-full max-h-36 overflow-hidden rounded-md border bg-gray-50 flex items-center justify-center">
                              <img src={logo} alt="Logo" className="w-full h-auto object-cover max-h-36" />
                            </div>
                          )}
                          <div className={`w-full ${textAlignClass}`}>
                            <h2 className="text-2xl font-bold">{formVersion.name || 'Form'}</h2>
                            {formVersion.description && (
                              <p className="text-sm text-muted-foreground mt-1">{formVersion.description}</p>
                            )}
                          </div>
                        </div>
                      );
                    }

                    // Default to 'top'
                    return (
                      <div className={`flex flex-col gap-3 ${alignClass}`}>
                        {logo && (
                          <div className="max-h-16 max-w-[200px] flex items-center justify-center mb-1">
                            <img src={logo} alt="Logo" className="max-h-16 max-w-full object-contain" />
                          </div>
                        )}
                        <div>
                          <h2 className="text-2xl font-bold">{formVersion.name || 'Form'}</h2>
                          {formVersion.description && (
                            <p className="text-sm text-muted-foreground mt-1">{formVersion.description}</p>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Progress Bar */}
                  <div className="mt-4 w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-primary h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(visiblePages.length > 0 ? ((visiblePages.indexOf(currentPageIndex) + 1) / visiblePages.length) : 0) * 100}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Current Page Content */}
              {currentPage && (
                <FormPageRenderer
                  page={currentPage}
                  fields={fields}
                  sections={sections}
                  answersRecord={answersRecord}
                  formVersion={formVersion}
                  readOnly={readOnly}
                  sectionCollapsedState={sectionCollapsedState}
                  currentPageIndex={currentPageIndex}
                  visiblePages={visiblePages}
                  submitting={submitting}
                  hasPreviousVisiblePage={hasPreviousVisiblePage}
                  hasNextVisiblePage={hasNextVisiblePage}
                  handleFieldChange={handleFieldChange}
                  handleSectionCollapseChange={handleSectionCollapseChange}
                  openCameraModal={openCameraModal}
                  openPaymentModal={openPaymentModal}
                  handlePreviousPage={handlePreviousPage}
                  handlePageSubmit={handlePageSubmit}
                  paymentFieldStatuses={paymentFieldStatuses}
                  // Same figure the modal gets: the amount the server bound to
                  // the transaction during pre-initialize. Without it the field
                  // falls back to amountConfig, which is 0 for a server-priced
                  // payment and renders as R0.00.
                  preloadedAmount={preloadedExternalPayment?.amount}
                  allFields={formVersion?.form_structure?.fields || []}
                  navigationMode={navigationMode}
                  allPages={pages}
                  onGoToPage={setCurrentPageIndex}
                />
              )}
            </div>
          </UnifiedCardContent>
        </UnifiedCard>

        {/* Camera Modal */}
        {cameraModalOpen && currentCameraField && (
          <CameraModal
            ref={webcamRef}
            isOpen={cameraModalOpen}
            onClose={() => {
              setCameraModalOpen(false);
              setCurrentCameraField(null);
              setCurrentPromptId(undefined);
            }}
            onCapture={handleCameraCapture}
            silhouette={
              currentCameraField.silhouetteUrl
                ? getResolvedSilhouetteUrl(currentCameraField.silhouetteUrl)
                : undefined
            }
            multiCapture={currentCameraField.type === "multi_image_capture"}
            maxImages={currentCameraField.max_images || 10}
            existingImages={
              currentCameraField.type === "multi_image_capture"
                ? (answers.get(currentCameraField.id) as File[] | CapturedImage[] | undefined) || []
                : []
            }
            prompts={currentCameraField.prompts}
            specificPromptId={currentPromptId}
          />
        )}

        {/* Payment Modal */}
        {currentPaymentField && (
          <PaymentModal
            isOpen={paymentModalOpen}
            fieldId={currentPaymentField?.id || ""}
            amount={currentPaymentField ? getPaymentAmount(currentPaymentField) : 0}
            currency={currentPaymentField?.amountConfig?.currency || "ZAR"}
            description={currentPaymentField?.paymentDescription}
            metadata={currentPaymentField?.paymentMetadata}
            formEntryId={propFormEntryId}
            formVersionId={formVersion?.id}
            onClose={closePaymentModal}
            onSuccess={handlePaymentSuccess}
            onError={handlePaymentError}
            paystackPublicKey={appConfig?.features?.payment?.paystackPublicKey}
            apiBaseUrl={appConfig?.api_base_url}
            customPaymentEndpoint={currentPaymentField?.customPaymentEndpoint}
            verificationEndpoint={currentPaymentField?.verificationEndpoint}
            planCode={currentPaymentField?.planConfig?.planCode || currentPaymentField?.amountConfig?.planCode}
            paymentMode={currentPaymentField?.paymentMode}
            preloadedAccessCode={preloadedExternalPayment?.accessCode}
            preloadedReference={preloadedExternalPayment?.reference}
            preloadedAmount={preloadedExternalPayment?.amount}
            // The form's own answers, keyed by field id. The modal's fallback
            // initialize built a synthetic answers object (email/name/phone
            // only), so a server that prices from a form answer — a billing
            // date, a quantity, a tier — could not see it and silently priced
            // from a default. Passing them makes that path carry the same
            // information the page-submission preload does.
            formAnswers={answersRecord}
            email={mappedValues.email}
            firstName={mappedValues.firstName}
            lastName={mappedValues.lastName}
            phone={mappedValues.phone}
            sessionId={sessionId || getFormSessionData()?.sessionId || preloadedExternalPayment?.sessionId}
            userId={userId || preloadedExternalPayment?.userId}
            formId={(formId || formVersion?.form?.id)?.toString()}
            pageNumber={currentPage?.order || currentPageIndex}
          />
        )}

        {/* Session Continuation Modal */}
        {showSessionModal && sessionModalConfig.enabled && (
          <Dialog
            open={showSessionModal}
            onOpenChange={(open) => {
              setShowSessionModal(open);
              if (!open) {
                setSessionDecisionMade(true);
              }
            }}
          >
            <DialogContent
              className={sessionModalConfig.modalClassName || "sm:max-w-md"}
              style={sessionModalConfig.modalStyle}
            >
              <DialogHeader>
                <DialogTitle>
                  {sessionModalConfig.title || "Continue Previous Session?"}
                </DialogTitle>
                <DialogDescription>
                  {sessionModalConfig.message || "We found a previous form session. Would you like to continue where you left off, or start a new form?"}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleStartNewSessionClick}
                  className={sessionModalConfig.startNewButtonClassName}
                >
                  {sessionModalConfig.startNewButtonText || "Start New Form"}
                </Button>
                <Button
                  type="button"
                  onClick={handleContinueSessionClick}
                  className={sessionModalConfig.continueButtonClassName}
                >
                  {sessionModalConfig.continueButtonText || "Continue Previous Session"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {shouldShowBranding && (
          <div
            id="platform-branding-footer"
            className="mt-8 text-center text-xs text-muted-foreground pb-4 flex items-center justify-center gap-1 select-none opacity-80 hover:opacity-100 transition-opacity"
          >
            <span>Powered by</span>
            <span className="font-semibold text-black">Form Builder</span>
          </div>
        )}

        <ConflictResolutionModal
          isOpen={showConflictModal}
          conflictData={conflictData}
          formFields={formVersion?.form_structure?.fields || []}
          onClose={() => setShowConflictModal(false)}
          onKeepLocal={handleKeepLocalChanges}
          onUseStorage={handleUseStorageChanges}
          onSmartMerge={handleSmartMerge}
        />
        </div>
      </ThemedRendererRoot>
    </StylingProvider>
  );
}
